import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Constants from "../../../../Constants.json";
import AuthContext from "../../store/auth-context";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  LabelList,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";

import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from "react-simple-maps";

import { FiClock, FiGrid, FiBell, FiSearch, FiLogOut, FiTable, FiMenu, FiX, FiBarChart2 } from "react-icons/fi";
import { FaUser } from "react-icons/fa";
import LogoInvelps from "../../../../assets/Original.svg";
import MarketFlowChartView from "./MarketFlowHeatmap.js";
import {
  useDrillFilterStyles,
  useDrillAssetFilters,
  DrillDownFilterBar,
} from "./DrillDownAssetFilters.js";
import { navigateToStockDashboard } from "../../../../utils/stockDashboardNav";
import DrillRowTip, { drillRowTitle } from "../../../../components/DrillRowTip";
import MarketPageHero from "../../../../components/MarketPageHero";
import { MARKET_PAGE_LAYOUT_CSS } from "../../../../styles/marketPageLayoutCss";
import useInvelpsTheme from "../../../../hooks/useInvelpsTheme";

/**
 * Market Flow (Top Down Flow)
 * - Single-file React component
 * - Inline CSS
 * - Flow view + Table view
 * - Charts: Sector rotation (stacked bars) + Styles radar
 * - World map: colored countries + continuous gradient legend + Top/Bottom 3 + Top/Bottom N labels
 */

/* ---------------- Translation Context ---------------- */
const TranslationContext = React.createContext();

function TranslationProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("language");
      return saved === "en" || saved === "fr" ? saved : "fr";
    }
    return "fr";
  });

  const toggleLanguage = (e) => {
    // Empêcher la propagation de l'événement si présent
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newLang = language === "fr" ? "en" : "fr";
    if (typeof window !== "undefined") {
      // Sauvegarder d'abord dans localStorage
      localStorage.setItem("language", newLang);
      // Recharger la page immédiatement - pas besoin de setState car la page va se recharger
      window.location.reload();
    }
  };

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  // Fonction pour traduire les données dynamiques (régions, secteurs, etc.)
  const translateData = (value) => {
    if (!value) return value;
    const translated = translations[language]?.[value];
    return translated || value;
  };


  // Fonction pour traduire le texte de la narrative (remplace les termes dans le texte)
  const translateNarrative = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Si on est en anglais, le texte de l'API est en français, donc on le garde tel quel
    // La traduction sera gérée dans le composant avec useState
    if (language === 'en') {
      return text; // Le texte sera traduit dans le composant
    }
    
    // Si on est en français, traduire les termes spécifiques
    let translatedText = text;
    
    // Liste des termes à traduire (dans l'ordre, les plus longs d'abord pour éviter les remplacements partiels)
    const terms = [
      'Southeast Europe', 'South America', 'North America', 'South Asia', 'North Asia',
      'Information Technology', 'Consumer Discretionary', 'Consumer Staples', 'Communication Services',
      'Real Estate', 'High_Quality', 'High Quality', 'Large Cap', 'Mega Cap', 'Mid Cap', 'Small Cap', 'Nano Cap',
      'Defensif', 'Quality', 'Growth', 'Value', 'Core', 'Income', 'Excellent', 'Good', 'Average', 'Bad', 'Worst',
      'Africa', 'Europe', 'Asia', 'Oceania', 'Middle East',
      // Ajouter les mois complets (avec et sans majuscule)
      'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
      // Ajouter les mois abrégés
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    
    terms.forEach(term => {
      const translation = translations[language]?.[term];
      if (translation && translation !== term) {
        // Remplacer le terme par sa traduction (insensible à la casse)
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Utiliser une regex simple qui détecte le terme comme mot complet
        // Utiliser \b pour word boundaries, mais aussi gérer les cas comme "En October"
        const regex = new RegExp(`\\b${escapedTerm}\\b`, 'gi');
        const beforeReplace = translatedText;
        translatedText = translatedText.replace(regex, (match) => {
          // Préserver la casse de la première lettre
          if (match[0] === match[0].toUpperCase()) {
            return translation.charAt(0).toUpperCase() + translation.slice(1);
          }
          return translation;
        });
        if (beforeReplace !== translatedText) {
          console.log(`🔄 Replaced "${term}" with "${translation}"`);
        }
      }
    });
    
    return translatedText;
  };

  // Mémoriser la valeur du contexte pour éviter les re-renders inutiles
  // mais permettre les re-renders quand language change
  const contextValue = useMemo(() => ({
    t,
    toggleLanguage,
    language,
    translateData,
    translateNarrative
  }), [language]); // Dépendre de language pour forcer un re-render quand il change

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
}

function useTranslation() {
  const context = useContext(TranslationContext);
  if (!context) {
    // Fallback si le context n'est pas disponible (ne devrait pas arriver)
    // On utilise une valeur par défaut sans hook pour éviter les erreurs de règles de hooks
    const language = (() => {
      if (typeof window !== "undefined") {
        const saved = localStorage.getItem("language");
        return saved === "en" || saved === "fr" ? saved : "fr";
      }
      return "fr";
    })();
    const t = (key) => translations[language]?.[key] || key;
    const translateData = (value) => {
      if (!value) return value;
      return translations[language]?.[value] || value;
    };
    const translateNarrative = (text) => text;
    const toggleLanguage = () => {
      console.warn('TranslationContext not available. Language toggle disabled.');
    };
    return { t, toggleLanguage, language, translateData, translateNarrative };
  }
  return context;
}

/* ---------------- Translation Hook ---------------- */
const translations = {
  fr: {
    marketLeaders: "Leaders du Marché",
    marketFlow: "Flux de Marché",
    marketFlows: "Flux de Marché",
    dataAsOf: "Date au",
    monthlyReturn: "Rendement Mensuel",
    deltaVsLastMonth: "Δ vs Mois Précédent",
    capitalNetFlow: "Capital Net Flow",
    netFlowPressure: "Net Flow Pressure",
    cnfLabel: "FCN",
    nfpLabel: "PNF",
    search: "Rechercher",
    switchToTableView: "Vue Table",
    switchToFlowView: "Vue Flow",
    switchToHeatmapView: "Vue Heatmap",
    regionFlows: "Région",
    sectorRotation: "Secteur",
    countryPerformance: "Pays",
    countryPerformanceSubtitle: "Carte du monde colorée par Performance",
    monthlyPerformanceSubtitle: "Performance mensuelle",
    capitalFlowSubtitle: "Flux de Capitaux nets",
    historicalView: "Vue historique — instantané du marché à la date sélectionnée",
    loadingMarketData: "Chargement des données du marché...",
    errorLoadingData: "Erreur de chargement des données",
    errorLoadingApi: "Impossible de charger les données depuis l'API.",
    retry: "Réessayer",
    marketRegime: "Régime de Marché",
    item: "Élément",
    monthlyReturnCol: "Rendement Mensuel",
    deltaVsLastMonthCol: "Δ vs Mois Précédent",
    regions: "Régions",
    countries: "Pays",
    sectors: "Secteurs",
    marketCap: "Capitalisation",
    fundamentalScore: "Fondamentaux",
    styles: "Styles",
    investingStyles: "Styles d'Investissement",
    investingStylesSubtitle: "",
    marketCapPerformance: "Capitalisation",
    noDataAvailable: "Aucune donnée disponible",
    time: "Temps",
    grid: "Grille",
    notifications: "Notifications",
    login: "Connexion",
    logout: "Déconnexion",
    menu: "Menu",
    seeMore: "Voir plus",
    seeLess: "Voir moins",
    backToTopDown: "Retour à la vue d'ensemble",
    scope: "Portée",
    assets: "actifs",
    asset: "Actif",
    tip: "Astuce",
    pressEscToClose: "Appuyez sur Esc pour fermer",
    // Traductions des mois
    January: "janvier",
    February: "février",
    March: "mars",
    April: "avril",
    May: "mai",
    June: "juin",
    July: "juillet",
    August: "août",
    September: "septembre",
    October: "octobre",
    November: "novembre",
    December: "décembre",
    Jan: "janv.",
    Feb: "févr.",
    Mar: "mars",
    Apr: "avr.",
    Jun: "juin",
    Jul: "juil.",
    Aug: "août",
    Sep: "sept.",
    Oct: "oct.",
    Nov: "nov.",
    Dec: "déc.",
    // Traductions des régions
    'North America': 'Amérique du Nord',
    'Europe': 'Europe',
    'Asia': 'Asie',
    'South America': 'Amérique du Sud',
    'Africa': 'Afrique',
    'Middle East': 'Moyen-Orient',
    'Oceania': 'Océanie',
    'North Asia': 'Asie du Nord',
    'South Asia': 'Asie du Sud',
    'Southeast Asia': 'Asie du Sud-Est',
    // Traductions des secteurs
    'Communication Services': 'Services de Communication',
    'Consumer Discretionary': 'Consommation Discrétionnaire',
    'Consumer Staples': 'Produits de Base',
    'Energy': 'Énergie',
    'Financials': 'Finance',
    'Health Care': 'Santé',
    'Industrials': 'Industrie',
    'Information Technology': 'Technologie de l\'Information',
    'Materials': 'Matériaux',
    'Real Estate': 'Immobilier',
    'Utilities': 'Services Publics',
    // Traductions des styles
    'Value': 'Valeur',
    'Growth': 'Croissance',
    'Momentum': 'Momentum',
    'Quality': 'Qualité',
    'Size': 'Taille',
    'Volatility': 'Volatilité',
    'Core': 'Core',
    'High_Quality': 'Haute Qualité',
    'Income': 'Revenu',
    'High Quality': 'Haute Qualité',
    // Termes de narrative
    'Defensif': 'Défensif',
    'Southeast Europe': 'Europe du Sud-Est',
    // Traductions des capitalisations
    'Mega Cap': 'Mega Cap',
    'Large Cap': 'Large Cap',
    'Mid Cap': 'Mid Cap',
    'Small Cap': 'Petite Cap',
    'Nano Cap': 'Nano Cap',
    // Traductions des scores fondamentaux
    'Excellent': 'Excellent',
    'Good': 'Bon',
    'Average': 'Moyen',
    'Bad': 'Mauvais',
    'Worst': 'Pire',
    // Labels de légende
    'Strong Inflows': 'Performance Forte',
    'High Outflows': 'Performance Faible',
    'Mild Outflows': 'Performance Modérée',
    'Moderate Inflows': 'Performance Modérée',
    'ACCUMULATION': 'ACCUMULATION',
    'DISTRIBUTION': 'DISTRIBUTION',
    'ROTATION – Sell Pressure': 'ROTATION – Pression vendeuse',
    'ROTATION – Buy Support': 'ROTATION – Support acheteur',
    'NEUTRAL – Low Signal': 'NEUTRE – Signal faible',
    'NEUTRAL – Volatile / Test': 'NEUTRAL – Volatile / Test',
    'Top 3': 'Top 3',
    'Bottom 3': 'Bas 3',
    'All Countries': 'Tous les pays',
    'Top/Bottom': 'Top/Bas',
    'Legend': 'Légende',
    'Labels': 'Labels',
    trendFollowing: "Tendance — 6 derniers mois",
    heatmapMode: "Heatmap",
    linesMode: "Lignes",
    heatmapEachColumnSorted: "chaque colonne triée DESC",
    sortedBySortKey: "tri par clé",
    reset: "Réinitialiser",
    topN: "Top N",
    all: "Tous",
    heatmapColumnSortedDesc: "Chaque colonne triée en décroissant.",
    heatmapHint: "Heatmap : chaque colonne triée en décroissant (leader en haut). Clic sur une cellule / pastille pour masquer ou afficher une série.",
    clickToHideShow: "Clic pour masquer/afficher",
    zoomCentralZone: "Zoom zone centrale",
    scaleAuto: "Échelle auto",
    sortLast: "Tri : Dernier",
    sortDelta1m: "Tri : Δ1M",
    sortSlope: "Tri : Pente",
  },
  en: {
    marketLeaders: "Market Leaders",
    marketFlow: "Market Flow",
    marketFlows: "Market Flows",
    dataAsOf: "Date as of",
    monthlyReturn: "Monthly Return",
    deltaVsLastMonth: "Δ vs Last Month",
    capitalNetFlow: "Capital Net Flow",
    netFlowPressure: "Net Flow Pressure",
    cnfLabel: "CNF",
    nfpLabel: "NFP",
    search: "Search",
    switchToTableView: "Table View",
    switchToFlowView: "Flow View",
    switchToHeatmapView: "Heatmap View",
    regionFlows: "Region",
    sectorRotation: "Sector",
    countryPerformance: "Country",
    countryPerformanceSubtitle: "World map colored by Performance",
    monthlyPerformanceSubtitle: "Monthly Performance",
    capitalFlowSubtitle: "Capital Net Flow",
    historicalView: "Historical view — market snapshot at selected date",
    loadingMarketData: "Loading market data...",
    errorLoadingData: "Error loading data",
    errorLoadingApi: "Unable to load data from API.",
    retry: "Retry",
    marketRegime: "Market Regime",
    item: "Item",
    monthlyReturnCol: "Monthly Return",
    deltaVsLastMonthCol: "Δ vs Last Month",
    regions: "Regions",
    countries: "Countries",
    sectors: "Sectors",
    marketCap: "Market Cap",
    fundamentalScore: "Fundamentals",
    styles: "Styles",
    investingStyles: "Investing Styles",
    investingStylesSubtitle: "",
    marketCapPerformance: "Market Cap",
    noDataAvailable: "No data available",
    time: "Time",
    grid: "Grid",
    notifications: "Notifications",
    login: "Login",
    logout: "Logout",
    menu: "Menu",
    seeMore: "See more",
    seeLess: "See less",
    backToTopDown: "Back to Top-Down",
    scope: "Scope",
    assets: "assets",
    asset: "Asset",
    tip: "Tip",
    pressEscToClose: "Press Esc to close",
    // Months (keep English)
    January: "January",
    February: "February",
    March: "March",
    April: "April",
    May: "May",
    June: "June",
    July: "July",
    August: "August",
    September: "September",
    October: "October",
    November: "November",
    December: "December",
    Jan: "Jan",
    Feb: "Feb",
    Mar: "Mar",
    Apr: "Apr",
    Jun: "Jun",
    Jul: "Jul",
    Aug: "Aug",
    Sep: "Sep",
    Oct: "Oct",
    Nov: "Nov",
    Dec: "Dec",
    // Regions (keep English)
    'North America': 'North America',
    'Europe': 'Europe',
    'Asia': 'Asia',
    'South America': 'South America',
    'Africa': 'Africa',
    'Middle East': 'Middle East',
    'Oceania': 'Oceania',
    'North Asia': 'North Asia',
    'South Asia': 'South Asia',
    'Southeast Asia': 'Southeast Asia',
    // Sectors (keep English)
    'Communication Services': 'Communication Services',
    'Consumer Discretionary': 'Consumer Discretionary',
    'Consumer Staples': 'Consumer Staples',
    'Energy': 'Energy',
    'Financials': 'Financials',
    'Health Care': 'Health Care',
    'Industrials': 'Industrials',
    'Information Technology': 'Information Technology',
    'Materials': 'Materials',
    'Real Estate': 'Real Estate',
    'Utilities': 'Utilities',
    // Styles (keep English)
    'Value': 'Value',
    'Growth': 'Growth',
    'Momentum': 'Momentum',
    'Quality': 'Quality',
    'Size': 'Size',
    'Volatility': 'Volatility',
    'Core': 'Core',
    'High_Quality': 'High Quality',
    'Income': 'Income',
    'High Quality': 'High Quality',
    'Southeast Europe': 'Southeast Europe',
    // Market Caps (keep English)
    'Mega Cap': 'Mega Cap',
    'Large Cap': 'Large Cap',
    'Mid Cap': 'Mid Cap',
    'Small Cap': 'Small Cap',
    'Nano Cap': 'Nano Cap',
    // Fundamental Scores (keep English)
    'Excellent': 'Excellent',
    'Good': 'Good',
    'Average': 'Average',
    'Bad': 'Bad',
    'Worst': 'Worst',
    // Legend labels
    'Strong Inflows': 'Strong Performance',
    'High Outflows': 'Weak Performance',
    'Mild Outflows': 'Moderate Performance',
    'Moderate Inflows': 'Moderate Performance',
    'ACCUMULATION': 'ACCUMULATION',
    'DISTRIBUTION': 'DISTRIBUTION',
    'ROTATION – Sell Pressure': 'ROTATION – Sell Pressure',
    'ROTATION – Buy Support': 'ROTATION – Buy Support',
    'NEUTRAL – Low Signal': 'NEUTRAL – Low Signal',
    'NEUTRAL – Volatile / Test': 'NEUTRAL – Volatile / Test',
    'Top 3': 'Top 3',
    'Bottom 3': 'Bottom 3',
    'All Countries': 'All Countries',
    'Top/Bottom': 'Top/Bottom',
    'Legend': 'Legend',
    'Labels': 'Labels',
    'Moderate Inflows': 'Moderate Inflows',
    trendFollowing: "Trend — last 6 months",
    heatmapMode: "Heatmap",
    linesMode: "Lines",
    heatmapEachColumnSorted: "each column sorted DESC",
    sortedBySortKey: "sorted by sort key",
    reset: "Reset",
    topN: "Top N",
    all: "All",
    heatmapColumnSortedDesc: "Each column sorted in descending order.",
    heatmapHint: "Heatmap: each column sorted descending (leader on top). Click a cell or chip to hide/show a series.",
    clickToHideShow: "Click to hide/show",
    zoomCentralZone: "Zoom central zone",
    scaleAuto: "Auto scale",
    sortLast: "Sort: Last",
    sortDelta1m: "Sort: Δ1M",
    sortSlope: "Sort: Slope",
  },
};


/* ---------------- Helpers ---------------- */
const fmtPct = (v) => `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
const fmtBp = (v) => `${v >= 0 ? "+" : ""}${Math.round(Number(v))}bp`;

// Couleurs pour la nouvelle légende Market Flow
const marketFlowColors = {
  ACCUMULATION: '#6F7F4E',           // Vert foncé
  DISTRIBUTION: '#8E4A43',           // Rouge
  'ROTATION_SELL': '#C2A24F',       // Jaune (Sell Pressure)
  'ROTATION_BUY': '#AEB9A2',        // Vert clair (Buy Support)
  'NEUTRAL_LOW': '#F2EFE6',         // Beige clair (Low Signal)
  'NEUTRAL_VOLATILE': '#C7B8A0',    // Beige (Volatile/Test)
  DEFAULT: '#e8e6df',                // Gris par défaut
};

// Fonction pour déterminer la catégorie et la couleur selon les nouvelles règles
function getMarketFlowCategory(cnfCum3m, nfpPct) {
  const cnf = Number(cnfCum3m) || 0;
  const nfp = Number(nfpPct) || 0;
  const absCnf = Math.abs(cnf);
  const absNfp = Math.abs(nfp);
  
  // ACCUMULATION: CNF_cum_3M ≥ +20 ET NFP_pct > 0
  if (cnf >= 20 && nfp > 0) {
    return { category: 'ACCUMULATION', color: marketFlowColors.ACCUMULATION };
  }
  
  // DISTRIBUTION: CNF_cum_3M ≤ −20 ET NFP_pct < 0
  if (cnf <= -20 && nfp < 0) {
    return { category: 'DISTRIBUTION', color: marketFlowColors.DISTRIBUTION };
  }
  
  // ROTATION – Sell Pressure: CNF_cum_3M > 0 ET NFP_pct < 0
  if (cnf > 0 && nfp < 0) {
    return { category: 'ROTATION – Sell Pressure', color: marketFlowColors.ROTATION_SELL };
  }
  
  // ROTATION – Buy Support: CNF_cum_3M < 0 ET NFP_pct > 0
  if (cnf < 0 && nfp > 0) {
    return { category: 'ROTATION – Buy Support', color: marketFlowColors.ROTATION_BUY };
  }
  
  // NEUTRAL – Low Signal: ABS(CNF_cum_3M) < 10 ET ABS(NFP_pct) < 3
  if (absCnf < 10 && absNfp < 3) {
    return { category: 'NEUTRAL – Low Signal', color: marketFlowColors.NEUTRAL_LOW };
  }
  
  // NEUTRAL – Volatile / Test: ABS(CNF_cum_3M) < 20 ET ABS(NFP_pct) ≥ 3
  if (absCnf < 20 && absNfp >= 3) {
    return { category: 'NEUTRAL – Volatile / Test', color: marketFlowColors.NEUTRAL_VOLATILE };
  }
  
  // Par défaut
  return { category: 'NEUTRAL – Low Signal', color: marketFlowColors.DEFAULT };
}

const tone = {
  green: "#6f8a5b",
  green2: "#8da57a",
  olive: "#a9b38a",
  amber: "#caa85e",
  orange: "#d28a52",
  red: "#c6523f",
};

// Gradient de couleurs continu basé sur la valeur avec échelle absolue intelligente
// 0 est toujours au centre (amber/jaune)
// Positif → vert (plus positif = plus vert)
// Négatif → rouge (plus négatif = plus rouge)
function colorByValue(v, type = 'percentage') {
  if (!Number.isFinite(v)) return tone.olive;
  
  // Définir les échelles absolues selon le type de métrique
  // Échelles serrées pour avoir des nuances même pour les valeurs positives faibles
  let min, max;
  if (type === 'percentage' || type === 'pct') {
    // Pour les pourcentages : -5% à +5% pour avoir beaucoup de nuances
    // Cela garantit que +1% est olive, +3% est vert clair, +5% est vert foncé
    min = -5;
    max = 5;
  } else if (type === 'basisPoints' || type === 'bp') {
    // Pour les basis points : -300bp à +300bp pour plus de nuances
    min = -300;
    max = 300;
  } else {
    // Par défaut, utiliser une échelle relative mais centrée sur 0
    // Calculer dynamiquement basé sur la valeur absolue
    const abs = Math.abs(v);
    const range = Math.max(3, abs * 1.5); // Au moins 3, ou 1.5x la valeur absolue
    min = -range;
    max = range;
  }
  
  // Clamper la valeur dans la plage
  const clamped = Math.max(min, Math.min(max, v));
  
  // Normaliser dans [0, 1] où 0 = min (rouge) et 1 = max (vert)
  // 0 absolu devrait être au milieu (amber)
  const normalized = clamp01((clamped - min) / (max - min));
  
  // Mapper sur le gradient : rouge (0) -> orange -> amber -> olive -> vert (1)
  const t = normalized;
  const seg = (HEAT_STOPS.length - 1) * t;
  const i = Math.min(HEAT_STOPS.length - 2, Math.max(0, Math.floor(seg)));
  const localT = seg - i;
  return lerpColor(HEAT_STOPS[i], HEAT_STOPS[i + 1], localT);
}

function safeNavigate(path) {
  try {
    if (typeof window !== "undefined") window.location.href = path;
  } catch {
    // no-op
  }
}

/* ---------------- Heatmap color scale ---------------- */
function hexToRgb(hex) {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHex(r, g, b) {
  const clamp = (x) => Math.max(0, Math.min(255, Math.round(x)));
  const to = (x) => clamp(x).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
function lerp(a, b, t) {
  return a + (b - a) * t;
}
function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}
function lerpColor(aHex, bHex, t) {
  const a = hexToRgb(aHex);
  const b = hexToRgb(bHex);
  return rgbToHex(lerp(a.r, b.r, t), lerp(a.g, b.g, t), lerp(a.b, b.b, t));
}

const HEAT_STOPS = [tone.red, tone.orange, tone.amber, tone.olive, tone.green2, tone.green];

function colorForValue(v, min, max) {
  if (!Number.isFinite(v) || !Number.isFinite(min) || !Number.isFinite(max) || min === max) return tone.olive;
  const t = clamp01((v - min) / (max - min));
  const seg = (HEAT_STOPS.length - 1) * t;
  const i = Math.min(HEAT_STOPS.length - 2, Math.max(0, Math.floor(seg)));
  const localT = seg - i;
  return lerpColor(HEAT_STOPS[i], HEAT_STOPS[i + 1], localT);
}

/* ---------------- Sample data removed - using API only ---------------- */

// MARKET_COMMENTARY est maintenant récupéré dynamiquement depuis l'API (momentumNarrative)

// World GeoJSON with ISO3 ids
const WORLD_GEO_URL = "https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson";

/* ---------------- Inline CSS ---------------- */
function Style() {
  return (
    <style>{`
      :root{
        --bg:#fbfaf7;
        --paper:#fff;
        --ink:#1f2a24;
        --muted:#6f7b74;
        --line:rgba(30,40,34,.10);
        --shadow:0 10px 30px rgba(16,24,20,.10);
        --shadow2:0 6px 16px rgba(16,24,20,.08);
        --gold:#b59a5b;
        --radius:18px;
      }
      *{box-sizing:border-box}
      html, body{
        height:100%;
        margin:0;
        padding:0;
        overflow-x:hidden;
        width:100%;
        max-width:100vw;
      }
      body{background:
        radial-gradient(1200px 400px at 50% 0%, rgba(181,154,91,.12), transparent 55%),
        linear-gradient(var(--bg), var(--bg));
        color:var(--ink);
        font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
        min-height:100vh;
        display:flex;
        flex-direction:column;
        overflow-x:hidden;
        width:100%;
        max-width:100vw;
      }
      #root{
        min-height:100vh;
        display:flex;
        flex-direction:column;
        overflow-x:hidden;
        width:100%;
        max-width:100vw;
      }
      html, body {
        overflow-x: hidden;
        width: 100%;
        max-width: 100vw;
        box-sizing: border-box;
      }
      * {
        box-sizing: border-box;
      }

      .td-wrap{
        max-width: min(1920px, 100%);
        margin: 0 auto;
        padding: 26px 32px 34px;
        flex: 1;
        width: 100%;
        box-sizing: border-box;
        overflow-x: hidden;
      }

      .td-header{
        position:fixed;
        top:0;
        left:0;
        right:0;
        z-index:1000;
        display:flex;
        justify-content:space-between;
        align-items:center;
        padding:20px 60px;
        width:100%;
        box-sizing:border-box;
        background: linear-gradient(135deg, #2d5a5a, #3a7a7a);
        box-shadow: 0 4px 12px rgba(0,0,0,.15);
        min-height:110px;
        gap:16px;
      }
      .td-headerSpacer{height:110px;}
      .td-headerLeft{display:flex;flex-direction:column;align-items:flex-start;justify-content:center;position:relative;}
      .td-brand{display:flex;align-items:center;cursor:pointer;transition:opacity 0.2s ease;height:60px;}
      .td-brand:hover{opacity:0.85;}
      .td-logo{height:60px;width:auto;display:flex;align-items:center;justify-content:center;}
      .td-logo img{height:100%;width:auto;object-fit:contain;filter:drop-shadow(0 3px 8px rgba(0,0,0,.4)) brightness(1.1) contrast(1.15);}
      .td-brandName{display:none;}
      .td-brandTagline{display:none;}
      .td-navTitles{display:flex;gap:24px;align-items:center;margin-left:0;margin-top:8px;}
      .td-navTitle{
        font-size:14px;
        font-weight:900;
        color:#fff;
        cursor:pointer;
        padding:6px 0;
        position:relative;
        transition:opacity 0.2s ease;
        letter-spacing:0.01em;
      }
      .td-navTitle:hover{opacity:0.85;}
      .td-navTitle.active::after{
        content:'';
        position:absolute;
        bottom:0;
        left:0;
        right:0;
        height:3px;
        background:linear-gradient(90deg, #b59a5b, #d4b87a);
        border-radius:2px 2px 0 0;
      }

      .td-headLeft{flex:1;display:flex;flex-direction:column;align-items:flex-start;gap:6px;min-width:280px;max-width:100%;}
      .td-headTitle{font-size:24px;font-weight:950;color:var(--ink);letter-spacing:-0.02em;}
      .td-headSub{font-size:13px;color:var(--muted);font-weight:500;}

      .td-actions{display:flex;flex-direction:row;align-items:center;gap:10px;flex-shrink:0;}
      .td-iconRow{display:flex;gap:8px;align-items:center;}
      .td-searchBar{display:flex;align-items:center;gap:8px;overflow:hidden;transition:all 0.3s ease;order:-1;}
      .td-searchBar.hidden{width:0;opacity:0;margin:0;padding:0;}
      .td-searchBar.visible{width:300px;opacity:1;margin-right:10px;}
      .td-searchInput{flex:1;padding:8px 12px;border-radius:8px;border:1px solid rgba(255,255,255,.3);background:rgba(255,255,255,.9);color:var(--ink);font-size:14px;outline:none;}
      .td-searchInput::placeholder{color:rgba(36,48,40,.5);}
      .td-iconBtn{
        width:40px;
        height:40px;
        border-radius:10px;
        border:1px solid rgba(255,255,255,.2);
        background: rgba(255,255,255,.15);
        backdrop-filter: blur(10px);
        cursor:pointer;
        display:flex;
        align-items:center;
        justify-content:center;
        color:#fff;
        font-size:18px;
        transition:all 0.2s ease;
        padding:0;
      }
      .td-iconBtn:hover{
        background: rgba(255,255,255,.25);
        border-color:rgba(255,255,255,.3);
        transform:translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,.2);
      }
      .td-burgerBtn{display:none;width:44px;height:44px;border-radius:12px;border:1px solid rgba(255,255,255,.25);background:rgba(255,255,255,.2);backdrop-filter:blur(10px);cursor:pointer;align-items:center;justify-content:center;color:#fff;font-size:22px;transition:all 0.3s ease;position:relative;z-index:1001;flex-shrink:0;}
      .td-burgerBtn:hover{background:rgba(255,255,255,.3);transform:scale(1.05);}
      .td-burgerBtn:active{transform:scale(0.95);}
      .td-mobileMenu{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.6);backdrop-filter:blur(4px);z-index:1000;opacity:0;visibility:hidden;transition:opacity 0.3s ease, visibility 0.3s ease;}
      .td-mobileMenu.open{opacity:1;visibility:visible;}
      .td-mobileMenuPanel{position:fixed;top:0;right:0;width:320px;max-width:85vw;height:100vh;background:linear-gradient(135deg, #2d5a5a, #3a7a7a);box-shadow:-4px 0 20px rgba(0,0,0,.3);z-index:1001;transform:translateX(100%);transition:transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;}
      .td-mobileMenu.open .td-mobileMenuPanel{transform:translateX(0);}
      .td-mobileMenuHeader{display:flex;justify-content:space-between;align-items:center;padding:20px;border-bottom:1px solid rgba(255,255,255,.15);flex-shrink:0;}
      .td-mobileMenuTitle{color:#fff;font-size:18px;font-weight:800;letter-spacing:0.5px;}
      .td-mobileMenuClose{width:40px;height:40px;border-radius:10px;border:1px solid rgba(255,255,255,.2);background:rgba(255,255,255,.1);cursor:pointer;display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;transition:all 0.2s ease;}
      .td-mobileMenuClose:hover{background:rgba(255,255,255,.2);}
      .td-mobileMenuContent{display:flex;flex-direction:column;padding:16px;gap:10px;flex:1;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;min-height:0;}
      .td-mobileSearchBar{padding:0 16px 16px;border-bottom:1px solid rgba(255,255,255,.15);margin-bottom:8px;}
      .td-mobileSearchInput{width:100%;padding:12px 16px;border-radius:10px;border:1px solid rgba(255,255,255,.3);background:rgba(255,255,255,.15);color:#fff;font-size:15px;outline:none;transition:all 0.2s ease;}
      .td-mobileSearchInput::placeholder{color:rgba(255,255,255,.6);}
      .td-mobileSearchInput:focus{background:rgba(255,255,255,.25);border-color:rgba(255,255,255,.5);}
      .td-mobileMenuItem{display:flex;align-items:center;gap:14px;padding:14px 18px;border-radius:12px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);color:#fff;font-size:16px;font-weight:600;cursor:pointer;transition:all 0.2s ease;text-align:left;width:100%;}
      .td-mobileMenuItem:hover{background:rgba(255,255,255,.18);border-color:rgba(255,255,255,.25);transform:translateX(4px);}
      .td-mobileMenuItem:active{transform:translateX(2px) scale(0.98);}
      .td-mobileMenuItem svg{font-size:20px;flex-shrink:0;}
      .td-mobilePrimaryBtn{background:rgba(255,255,255,.2);font-weight:800;border-color:rgba(255,255,255,.3);margin-top:8px;}
      .td-mobilePrimaryBtn:hover{background:rgba(255,255,255,.3);}
      .td-mobileMenuFooter{padding:16px;border-top:1px solid rgba(255,255,255,.15);margin-top:auto;flex-shrink:0;}
      ${MARKET_PAGE_LAYOUT_CSS}
      .td-primaryBtn{
        width:240px;
        padding:12px 14px;
        border-radius:12px;
        border:1px solid rgba(181,154,91,.30);
        background: linear-gradient(#fbf7ee, #f3eddf);
        color:#4a3d22;
        font-weight:800;
        box-shadow: var(--shadow2);
        cursor:pointer;
        display:flex;
        align-items:center;
        justify-content:center;
        gap:8px;
        transition:all 0.2s ease;
      }
      .td-primaryBtn:hover{
        background: linear-gradient(#f3eddf, #ebe4d0);
        transform:translateY(-1px);
        box-shadow: 0 10px 24px rgba(16,24,20,.12);
      }
      .td-viewIconBtn{
        width:44px;
        height:44px;
        min-width:44px;
        min-height:44px;
        padding:0;
        border-radius:12px;
        border:1px solid rgba(181,154,91,.30);
        background: linear-gradient(#fbf7ee, #f3eddf);
        color:#4a3d22;
        font-weight:800;
        box-shadow: var(--shadow2);
        cursor:pointer;
        display:flex;
        align-items:center;
        justify-content:center;
        transition:all 0.2s ease;
      }
      .td-viewIconBtn:hover{
        background: linear-gradient(#f3eddf, #ebe4d0);
        transform:translateY(-1px);
        box-shadow: 0 10px 24px rgba(16,24,20,.12);
      }
      .td-viewIconBtn svg{font-size:20px;}

      .td-controls{display:flex;gap:12px;align-items:stretch;flex-wrap:nowrap;justify-content:flex-start;width:100%;max-width:100%;}

      .td-pill{display:flex;flex-direction:column;gap:4px;padding:10px 12px;border-radius:14px;background: rgba(255,255,255,.8);
        border:1px solid var(--line); box-shadow: var(--shadow2); min-width:160px;align-items:flex-start;}      
      .td-pillLabel{font-size:12px;color:var(--muted);text-align:left;}
      .td-pillLabelDesktop{display:block;}
      .td-pillLabelMobile{display:none;}
      .td-rowHeaderDesktop{display:block;}
      .td-rowHeaderMobile{display:none;}
      .td-gold{color:var(--gold);font-weight:900;}

      .td-select{
        padding:10px 12px;
        padding-right:36px;
        border-radius:12px;
        border:1px solid var(--line);
        background:#fff;
        color:var(--ink);
        font-size:14px;
        font-weight:600;
        cursor:pointer;
        outline:none;
        appearance:none;
        -webkit-appearance:none;
        -moz-appearance:none;
        background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23243028' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
        background-repeat:no-repeat;
        background-position:right 12px center;
        background-size:12px 8px;
        transition:all 0.2s ease;
        margin-bottom:16px;
      }
      .td-select:hover{
        border-color:rgba(181,154,91,.4);
        background-color:#fbf7ee;
      }
      .td-select:focus{
        border-color:rgba(181,154,91,.6);
        box-shadow:0 0 0 3px rgba(181,154,91,.1);
        background-color:#fff;
      }
      .td-select:active{
        border-color:rgba(181,154,91,.5);
      }
      /* iOS Safari specific fixes */
      @supports (-webkit-touch-callout: none) {
        .td-select{
          -webkit-appearance:none;
          appearance:none;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23243028' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
          background-repeat:no-repeat;
          background-position:right 12px center;
          background-size:12px 8px;
          padding-right:36px;
        }
      }
      /* Android Chrome specific fixes */
      @media screen and (-webkit-min-device-pixel-ratio: 0) {
        .td-select{
          -webkit-appearance:none;
          appearance:none;
        }
      }

      .td-grid{
        display:grid;
        grid-template-columns:3.5fr 1fr;
        gap:24px;
        align-items:start;
        width:100%;
        max-width:100%;
        box-sizing:border-box;
      }
      .td-col{display:flex;flex-direction:column;gap:18px;}

      .td-card{background:var(--paper);border:1px solid var(--line);border-radius: var(--radius);box-shadow: var(--shadow);
        padding:18px 20px 18px;overflow:hidden;}
      .td-cardHead{display:flex;justify-content:space-between;gap:10px;margin-bottom:12px;}
      .td-cardTitle h2{margin:0;font-size:18px;font-weight:900;color:var(--ink);letter-spacing:-0.01em;}
      .td-cardSub{margin-top:4px;font-size:11px;color:var(--muted);font-weight:500;}

      .td-rows{display:flex;flex-direction:column;gap:10px;}
      .td-rowHeader{width:100%;display:grid;grid-template-columns: 1fr 110px 160px;gap:12px;align-items:center;
        padding:8px 14px 4px;margin-bottom:4px;}
      .td-rowHeaderLabel{font-weight:800;color:var(--muted);text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-rowHeaderVal{text-align:center;font-weight:800;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-rowHeaderPill{text-align:center;font-weight:800;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-row{width:100%;display:grid;grid-template-columns: 1fr 110px 160px;gap:12px;align-items:center;
        padding:12px 14px;border-radius:14px;background: linear-gradient(90deg, rgba(181,154,91,.08), transparent 65%);
        border:1px solid rgba(30,40,34,.08);min-height:52px;cursor:pointer;text-align:left;}
      .td-rowLabel{font-weight:900;color:var(--ink);text-align:left;font-size:14px;}
      .td-rowVal{text-align:center;font-weight:950;font-variant-numeric:tabular-nums;font-size:14px;color:var(--ink);}
      .td-rowPill{text-align:center;font-weight:950;font-variant-numeric:tabular-nums;font-size:14px;color:var(--ink);}      
      .td-rowPill.strong{background:linear-gradient(135deg, rgba(111,138,91,.92), rgba(141,165,122,.86))}
      .td-rowPill.moderate{background:linear-gradient(135deg, rgba(202,168,94,.92), rgba(181,154,91,.82))}
      .td-rowPill.outflow{background:linear-gradient(135deg, rgba(198,82,63,.92), rgba(210,138,82,.78))}

      .td-tri{width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;}
      .td-tri.up{border-bottom:10px solid rgba(255,255,255,.92)}
      .td-tri.down{border-top:10px solid rgba(255,255,255,.92)}

      .td-chartBox{border-radius:14px;border:1px solid rgba(30,40,34,.08);background:#fff;padding:14px;}

      .td-styles{display:grid;grid-template-columns:1fr 260px;gap:12px;align-items:stretch;}
      .td-styleRows{display:flex;flex-direction:column;gap:10px;}
      .td-styleHeader{display:grid;grid-template-columns: 100px 1fr 80px 80px;gap:12px;align-items:center;
        padding:8px 14px 4px;margin-bottom:4px;}
      .td-styleHeaderLabel{font-weight:800;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-styleHeaderBar{font-weight:800;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-styleHeaderPct{text-align:center;font-weight:800;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-styleHeaderDelta{text-align:center;font-weight:800;color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:0.05em;}
      .td-styleRow{display:grid;grid-template-columns: 100px 1fr 80px 80px;gap:12px;align-items:center;
        padding:12px 14px;border-radius:14px;border:1px solid rgba(30,40,34,.08);background:#fff;}
      .td-styleName{font-weight:900;font-size:14px;color:var(--ink);}
      .td-styleBar{height:8px;border-radius:999px;background: rgba(30,40,34,.08);overflow:hidden;}
      .td-styleBar i{display:block;height:100%;border-radius:999px;background: linear-gradient(90deg, rgba(111,138,91,.9), rgba(181,154,91,.85));}
      .td-stylePct{text-align:center;font-weight:900;font-size:13px;color:var(--ink);}
      .td-styleViz{border-radius:16px;border:1px solid rgba(30,40,34,.08);background: radial-gradient(220px 180px at 50% 45%, rgba(111,138,91,.12), rgba(181,154,91,.06) 55%, rgba(255,255,255,1) 70%);}

      .td-mapWrap{border-radius:16px;border:1px solid rgba(30,40,34,.08);background: radial-gradient(700px 260px at 50% 40%, rgba(181,154,91,.13), transparent 65%);
        padding:10px;}
      .td-map{width:100%;height:460px;border-radius:14px;overflow:hidden;background: #fff;border:1px solid rgba(30,40,34,.06);}      

      .td-kpiBar{display:flex;gap:20px;flex-wrap:wrap;color:var(--muted);margin-top:12px;font-size:13px;padding:8px 2px 0;font-weight:600;}
      .td-kpiBar b{color:var(--ink);font-weight:900;}

      .td-kpis{display:grid;grid-template-columns: repeat(5, 1fr);gap:12px;}
      .td-kpi{border-radius:14px;padding:16px 14px 12px;color:#fff;box-shadow:0 10px 22px rgba(16,24,20,.14);min-height:110px;
        display:flex;flex-direction:column;justify-content:space-between;}
      .td-kpi.green{background:linear-gradient(135deg, rgba(111,138,91,.95), rgba(141,165,122,.88))}
      .td-kpi.olive{background:linear-gradient(135deg, rgba(141,165,122,.92), rgba(181,154,91,.78))}
      .td-kpi.orange{background:linear-gradient(135deg, rgba(210,138,82,.94), rgba(202,168,94,.80))}
      .td-kpi.red{background:linear-gradient(135deg, rgba(198,82,63,.94), rgba(210,138,82,.82))}
      .td-kpiTitle{font-weight:900;font-size:13px;letter-spacing:0.01em;opacity:0.96;line-height:1.3;}
      .td-kpiVal{font-size:26px;font-weight:950;font-variant-numeric:tabular-nums;margin:8px 0 6px;line-height:1.2;}
      .td-kpiSub{display:flex;align-items:center;justify-content:space-between;font-size:12px;font-weight:900;opacity:.92;margin-top:6px;}
      .td-kpiTag{padding:4px 10px;border-radius:12px;background: rgba(255,255,255,.22);border:1px solid rgba(255,255,255,.28);font-size:11px;font-weight:800;}

      /* table view (equal heights + vertical scroll only) */
      .td-tableGrid{
        display:grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap:24px;
        align-items:stretch;
        grid-auto-rows:1fr;
        width:100%;
        max-width:100%;
        box-sizing:border-box;
      }
      .td-tableCard{background:var(--paper);border:1px solid var(--line);border-radius: var(--radius);box-shadow: var(--shadow);
        padding:14px;overflow:hidden;display:flex;flex-direction:column;height:580px;}
      .td-tableHead{display:flex;gap:10px;align-items:center;justify-content:space-between;margin-bottom:10px;flex-shrink:0;}
      .td-tableTitle{font-weight:950}
      .td-search{width:180px;padding:10px 12px;border-radius:12px;border:1px solid var(--line);background:#fff;}
      .td-tableWrap{overflow-x:hidden;overflow-y:auto;flex:1;min-height:0;max-height:532px;}
      .td-tableWrap-noScroll{overflow-y:visible !important;max-height:none !important;}
      .td-tableCard-noScroll{height:auto !important;}
      .td-table{width:100%;border-collapse:collapse;font-size:13px;table-layout:fixed;display:table;}
      .td-table thead{display:table-header-group;position:sticky;top:0;background:var(--paper);z-index:1;}
      .td-table tbody{display:table-row-group;}
      .td-table th,.td-table td{padding:10px 10px;border-bottom:1px solid rgba(30,40,34,.08);white-space:nowrap;height:41px;box-sizing:border-box;}
      .td-table th{color:#3a463f;font-size:12px;letter-spacing:.04em;text-transform:uppercase;user-select:none;text-align:left;background:var(--paper);}
      .td-table td{text-align:left;overflow:hidden;text-overflow:ellipsis;}
      .td-table th:nth-child(1), .td-table td:nth-child(1){width:42%;}
      .td-table th:nth-child(2), .td-table td:nth-child(2){width:29%;text-align:right;}
      .td-table th:nth-child(3), .td-table td:nth-child(3){width:29%;text-align:right;}
      .td-table tr:hover td{background: rgba(181,154,91,.08)}

      /* Trend / Heatmap view (Vue Heatmap) */
      .td-trendGrid{display:grid;grid-template-columns: 1fr 1fr;gap:24px;align-items:stretch;}
      .td-trendBox{border-radius:18px;border:1px solid rgba(30,40,34,.10);background: rgba(255,255,255,.86);
        box-shadow: 0 10px 26px rgba(16,24,20,.06); overflow:hidden;min-width:0;}
      .td-trendHead{display:flex;align-items:center;justify-content:space-between;gap:10px;
        padding: 20px 25px; border-bottom: 1px solid rgba(30,40,34,.10);flex-wrap:wrap;}
      .td-trendTitle{font-weight:950;color:var(--ink);}
      .td-trendMeta{font-size:12px;color: var(--muted); font-weight: 900;}
      .td-trendControls{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
      .td-trendInner{padding: 25px;min-width:0;}
      .td-trendTableWrap{margin-top: 10px; overflow-x:auto; overflow-y:hidden; -webkit-overflow-scrolling:touch;
        border-radius: 14px; border:1px solid rgba(30,40,34,.10); background:#fff; max-width:100%;}
      .td-legend{display:flex;flex-wrap:wrap;gap:10px;row-gap:8px;margin-top:10px;}
      .td-chip{display:inline-flex;align-items:center;gap:8px;border-radius:999px;padding:6px 10px;border:1px solid rgba(30,40,34,.10);
        background: rgba(181,154,91,.10); cursor:pointer; max-width:100%;}
      .td-chip.off{background:var(--line2);}
      .td-chip span{max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:900;color:var(--muted)}
      .td-chip.off span{color:var(--muted);text-decoration:line-through}
      .td-miniSelect{padding:8px 10px;border-radius:12px;border:1px solid rgba(30,40,34,.12); background:#fff; font-weight: 900; font-size: 12px;}
      .td-ghostBtn{padding:8px 10px;border-radius:12px;border:1px solid rgba(30,40,34,.12); background: rgba(255,255,255,.75); font-weight: 900; font-size: 12px; cursor:pointer;}

      /* ---------------- Drill modal ---------------- */
      .td-modalOverlay{
        position:fixed; inset:0;
        background: rgba(18,22,19,.35);
        backdrop-filter: blur(6px);
        display:flex; align-items:center; justify-content:center;
        padding: 18px;
        z-index: 9999;
      }
      .td-modal{
        width: min(1120px, 94vw);
        max-height: 86vh;
        border-radius: 18px;
        background: rgba(255,255,255,.90);
        border: 1px solid rgba(30,40,34,.14);
        box-shadow: 0 20px 55px rgba(16,24,20,.22);
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }
      .td-modalHeader{
        display:flex; align-items:flex-start; justify-content:space-between;
        gap: 14px;
        padding: 16px 18px 10px;
        border-bottom: 1px solid rgba(30,40,34,.10);
        flex-shrink: 0;
      }
      .td-modalTitle{display:flex;flex-direction:column;gap:4px;}
      .td-modalTitle h3{margin:0;font-size:22px;font-weight:950;color:var(--ink);}
      .td-modalMeta{font-size:12px;color: var(--muted); font-weight: 800;}
      .td-modalTopRow{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
      .td-backMini{
        display:inline-flex;align-items:center;gap:8px;
        padding:10px 14px;border-radius:12px;
        border:1px solid rgba(181,154,91,.25);
        background: rgba(255,255,255,.85);
        box-shadow: var(--shadow2);
        font-weight: 900;
        color:#4a3d22;
        cursor:pointer;
        font-size: 13px;
        transition: all 0.2s ease;
      }
      .td-backMini:hover{
        background: rgba(255,255,255,.95);
        border-color: rgba(181,154,91,.35);
        transform: translateX(-2px);
      }
      .td-modalClose{
        width: 38px; height: 38px; border-radius: 12px;
        border: 1px solid rgba(181,154,91,.25);
        background: rgba(255,255,255,.70);
        box-shadow: var(--shadow2);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        color: var(--ink);
        border: none;
      }
      .td-modalBody{
        padding: 14px 18px 18px;
        overflow:auto;
        max-height: calc(86vh - 68px);
        flex: 1;
        min-height: 0;
      }

      .td-scopeBar{
        display:flex; gap: 10px; flex-wrap:wrap;
        padding: 10px 12px;
        border-radius: 14px;
        border: 1px solid rgba(30,40,34,.10);
        background: rgba(255,255,255,.78);
        margin-bottom: 12px;
      }
      .td-scopeKpi{
        display:flex; flex-direction:column; gap: 4px;
        min-width: 220px;
      }
      .td-scopeKpi span{ font-size: 12px; color: var(--muted); font-weight: 900; }
      .td-scopeKpi b{ font-size: 16px; font-weight: 950; font-variant-numeric: tabular-nums; }

      .td-drillTable{
        text-align: center;
        width:100%;
        border-collapse: collapse;
        table-layout: fixed;
        font-size: 13px;
        border: 1px solid rgba(30,40,34,.10);
        border-radius: 14px;
        overflow:hidden;
      }
      .td-drillTable th, .td-drillTable td{
        padding: 10px 10px;
        border-bottom: 1px solid rgba(30,40,34,.08);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .td-drillTable th{
        background: rgba(181,154,91,.10);
        color:#3a463f;
        font-size: 12px;
        font-weight: 950;
        letter-spacing: .03em;
        text-transform: uppercase;
      }
      .td-drillTable tr:hover td{ background: rgba(181,154,91,.08); }
      .td-num{ font-weight: 950; font-variant-numeric: tabular-nums; }

      .td-badge{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        padding:6px 10px;
        border-radius:999px;
        font-weight:950;
        font-size:12px;
        border:1px solid rgba(30,40,34,.10);
        white-space:nowrap;
      }
      .td-badgeCap{
        background: rgba(181,154,91,.12);
        color:#4a3d22;
      }
      .td-badgeFund{
        color:#fff;
        border:1px solid rgba(255,255,255,.18);
      }

      /* mobile modal */
      @media (max-width: 720px){
        .td-modalOverlay{ padding: 0; }
        .td-modal{
          width: 100vw;
          height: 100vh;
          max-height: none;
          border-radius: 0;
        }
        .td-modalBody{
          max-height: none;
          padding: 12px 14px 16px;
        }
        .td-scopeKpi{ min-width: 46%; }
      }

      @media (max-width: 1400px){
        .td-grid{grid-template-columns:2.5fr 1fr;gap:20px;}
        .td-kpis{grid-template-columns: repeat(3, 1fr);}
        .td-chartBox{padding:20px;}
      }

      @media (max-width: 1200px){
        .td-wrap{padding:22px 18px 28px;}
        .td-grid{grid-template-columns:1fr;gap:20px;}
        .td-tableGrid{grid-template-columns:1fr;}
        .td-tableCard{height:580px;}
        .td-trendGrid{grid-template-columns:1fr;}
        .td-tableWrap{max-height:532px;}
        .td-actions{min-width:200px}
        .td-kpis{grid-template-columns: repeat(2, 1fr);gap:10px;}
        .td-styles{grid-template-columns:1fr;}
        .td-map{height:360px;}
        .td-chartBox{padding:18px;}
        .td-col{gap:16px;}
        .td-rows{gap:8px;}
        .td-rowHeader{grid-template-columns: 1fr 100px 150px;gap:10px;padding:6px 12px 2px;}
        .td-row{grid-template-columns: 1fr 100px 150px;gap:10px;padding:10px 12px;}
        .td-rowLabel{font-size:13px;}
        .td-rowVal{font-size:13px;text-align:center;}
        .td-rowPill{font-size:13px;text-align:center;}
        .td-styleHeader{grid-template-columns: 90px 1fr 70px 70px;gap:10px;padding:6px 12px 2px;}
        .td-cardTitle h2{font-size:17px;}
        .td-brandName{font-size:14px;}
        .td-headTitle{font-size:19px;}
        .td-primaryBtn{width:200px;padding:10px 12px;font-size:13px;}
        .td-iconBtn{width:32px;height:32px;font-size:15px;}
        .td-pill{min-width:140px;padding:8px 10px;}
        .td-pillLabel{font-size:11px;}
        .td-toolbar{margin:0 4px 12px;}
        .td-select{width:160px;padding:8px 10px;font-size:13px;}
        .td-kpiTitle{font-size:12px;}
        .td-kpiVal{font-size:22px;}
        .td-kpiSub{font-size:11px;}
        .td-styleRow{grid-template-columns: 90px 1fr 70px 70px;gap:10px;padding:10px 12px;}
        .td-styleName{font-size:13px;}
        .td-stylePct{font-size:12px;}
        .td-styleRows{gap:8px;}
      }

      @media (max-width: 1000px){
        .td-actions{display:none !important;}
        .td-burgerBtn{display:flex !important;}
        .td-pillLabelDesktop{display:none;}
        .td-pillLabelMobile{display:block;}
        .td-rowHeaderDesktop{display:none;}
        .td-rowHeaderMobile{display:block;}
      }

      @media (max-width: 768px){
        .td-wrap{padding:16px 12px 20px;}
        .td-header{padding:16px 20px;min-height:90px;gap:12px;}
        .td-headerSpacer{height:90px;}
        .td-headerContent{flex-direction:column;gap:16px;padding:16px;}
        .td-headLeft{min-width:auto;}
        .td-actions{display:none !important;}
        .td-burgerBtn{display:flex !important;width:40px;height:40px;font-size:20px;flex-shrink:0;}
        .td-primaryBtn{width:100%;}
        .td-headerLeft{min-width:auto;flex-shrink:1;}
        .td-brand{margin-left:0;min-width:auto;height:44px;}
        .td-logo{height:44px;}
        .td-navTitles{margin-top:6px;margin-left:-30px;gap:10px;}
        .td-navTitle{font-size:13px;}
        .td-mobileMenu{top:70px;}
        .td-controls{flex-direction:row;gap:8px;flex-wrap:wrap;}
        .td-pill{min-width:auto;flex:1;min-width:140px;}
        .td-pillLabelDesktop{display:none;}
        .td-pillLabelMobile{display:block;}
        .td-rowHeaderDesktop{display:none;}
        .td-rowHeaderMobile{display:block;}
        .td-kpis{grid-template-columns:1fr;gap:10px;}
        .td-map{height:280px;}
        .td-card{padding:14px 16px;}
        .td-cardTitle h2{font-size:16px;}
        .td-cardSub{font-size:10px;}
        .td-tableCard{height:580px;}
        .td-tableWrap{max-height:532px;}
        .td-trendGrid{grid-template-columns:1fr;}
        .td-trendBox{padding:0;}
        .td-trendInner{padding:8px 10px 10px;}
        .td-styles{grid-template-columns:1fr;gap:10px;}
        .td-styleRow{grid-template-columns:80px 1fr 60px 60px;gap:8px;padding:10px 12px;font-size:12px;}
        .td-styleName{font-size:12px;}
        .td-stylePct{font-size:11px;}
        .td-styleRows{gap:8px;}
        .td-chartBox{padding:16px;}
        .td-col{gap:14px;}
        .td-rows{gap:8px;}
        .td-rowHeader{grid-template-columns: 1fr 90px 130px;gap:8px;padding:6px 12px 2px;}
        .td-row{grid-template-columns: 1fr 90px 130px;gap:8px;padding:10px 12px;font-size:13px;}
        .td-rowLabel{font-size:12px;}
        .td-rowVal{font-size:12px;text-align:center;}
        .td-rowPill{font-size:12px;text-align:center;}
        .td-styleHeader{grid-template-columns:80px 1fr 60px 60px;gap:8px;padding:6px 12px 2px;}
        .td-brand{min-width:auto;gap:8px;}
        .td-logo{width:30px;height:30px;}
        .td-brandName{font-size:13px;}
        .td-headTitle{font-size:18px;}
        .td-headSub{font-size:11px;}
        .td-iconRow{gap:8px;}
        .td-iconBtn{width:30px;height:30px;font-size:14px;}
        .td-toolbar{flex-wrap:wrap;gap:8px;margin:0 2px 10px;}
        .td-select{width:100%;max-width:200px;}
        .td-kpiTitle{font-size:11px;}
        .td-kpiVal{font-size:20px;margin:6px 0 4px;}
        .td-kpiSub{font-size:10px;margin-top:4px;}
        .td-kpiTag{font-size:10px;padding:3px 8px;}
        .td-kpi{padding:14px 12px 10px;min-height:100px;}
        .td-kpiBar{font-size:12px;gap:12px;margin-top:10px;}
        .td-tableHead{flex-wrap:wrap;gap:8px;}
        .td-tableTitle{font-size:14px;}
        .td-search{width:100%;max-width:180px;padding:8px 10px;font-size:12px;}
        .td-table{font-size:12px;}
        .td-table th, .td-table td{padding:8px 8px;}
        .td-table th{font-size:11px;}
        .td-narrative-text{display:-webkit-box;-webkit-line-clamp:5;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;}
        .td-narrative-text.expanded{display:block;-webkit-line-clamp:none;}
        .td-seeMoreBtn{display:block;}
      }
      @media (min-width: 769px){
        .td-seeMoreBtn{display:none !important;}
        .td-narrative-text{display:block !important;-webkit-line-clamp:none !important;}
      }

      @media (max-width: 480px){
        .td-wrap{padding:12px 10px 16px;}
        .td-header{padding:12px 16px;min-height:80px;gap:10px;}
        .td-headerSpacer{height:80px;}
        .td-burgerBtn{display:flex !important;width:36px;height:36px;font-size:18px;flex-shrink:0;}
        .td-headerLeft{margin-left:25px;min-width:auto;flex-shrink:1;}
        .td-brand{margin-left:0;min-width:auto;height:auto;}
        .td-navTitles{margin-top:4px;margin-left:-30px;gap:10px;}
        .td-navTitle{font-size:12px;}
        .td-grid{gap:14px;}
        .td-col{gap:12px;}
        .td-rows{gap:6px;}
        .td-map{height:240px;}
        .td-kpiVal{font-size:18px;}
        .td-rowHeader{grid-template-columns:1fr 80px 120px;gap:6px;padding:5px 10px 2px;}
        .td-row{grid-template-columns:1fr 80px 120px;gap:6px;padding:8px 10px;font-size:12px;min-height:48px;}
        .td-rowLabel{font-size:11px;}
        .td-rowVal{font-size:11px;text-align:center;}
        .td-rowPill{font-size:11px;text-align:center;}
        .td-styleHeader{grid-template-columns:70px 1fr 50px 50px;gap:6px;padding:5px 10px 2px;}
        .td-card{padding:12px 14px;}
        .td-cardTitle h2{font-size:15px;}
        .td-cardSub{font-size:9px;margin-top:2px;}
        .td-chartBox{padding:12px;}
        .td-logo{width:28px;height:28px;}
        .td-brandName{font-size:12px;}
        .td-headTitle{font-size:16px;}
        .td-headSub{font-size:10px;}
        .td-iconRow{flex-wrap:wrap;gap:6px;}
        .td-iconBtn{width:28px;height:28px;font-size:13px;}
        .td-primaryBtn{padding:10px 12px;font-size:12px;}
        .td-pill{padding:8px 10px;}
        .td-pillLabel{font-size:10px;}
        .td-pillLabelDesktop{display:none;}
        .td-pillLabelMobile{display:block;}
        .td-rowHeaderDesktop{display:none;}
        .td-rowHeaderMobile{display:block;}
        .td-toolbar{flex-direction:column;align-items:stretch;gap:6px;}
        .td-select{width:100%;max-width:100%;padding:8px 10px;font-size:12px;}
        .td-mobileMenu{top:64px;}
        .td-kpiTitle{font-size:10px;}
        .td-kpiVal{font-size:16px;margin:4px 0 2px;}
        .td-kpiSub{font-size:9px;margin-top:2px;}
        .td-kpiTag{font-size:9px;padding:2px 6px;}
        .td-kpi{padding:12px 10px 8px;min-height:90px;}
        .td-kpiBar{font-size:11px;gap:8px;margin-top:8px;flex-direction:column;align-items:flex-start;}
        .td-styles{gap:8px;}
        .td-styleRow{grid-template-columns:70px 1fr 50px 50px;gap:6px;padding:8px 10px;font-size:11px;}
        .td-styleName{font-size:11px;}
        .td-stylePct{font-size:10px;}
        .td-styleBar{height:6px;}
        .td-styleRows{gap:6px;}
        .td-tableCard{height:580px;padding:12px;}
        .td-tableWrap{max-height:532px;}
        .td-tableHead{flex-direction:column;align-items:stretch;}
        .td-tableTitle{font-size:13px;}
        .td-search{width:100%;max-width:100%;padding:8px 10px;font-size:11px;}
        .td-table{font-size:11px;}
        .td-table th, .td-table td{padding:6px 4px;}
        .td-table th{font-size:10px;}
        .td-table th:nth-child(1), .td-table td:nth-child(1){width:40%;}
        .td-table th:nth-child(2), .td-table td:nth-child(2){width:30%;}
        .td-table th:nth-child(3), .td-table td:nth-child(3){width:30%;}
      }
    `}</style>
  );
}

/* ---------------- lightweight self-tests ---------------- */
function runSelfTests() {
  if (typeof window === "undefined") return;
  try {
    // Tests for utility functions only (SAMPLE removed - using API only)
    console.assert(fmtBp(0) === "+0bp", "fmtBp(0) should include + sign");
    console.assert(fmtPct(0) === "+0.00%", "fmtPct(0) should include + sign");
    console.assert(colorForValue(0, -10, 10).startsWith("#"), "colorForValue should return hex");
    console.assert(HEAT_STOPS.length >= 2, "HEAT_STOPS should have at least 2 colors");
  } catch {
    // ignore
  }
}

/* ---------------- Main Page ---------------- */
function Landing() {
  useInvelpsTheme();
  const navigate = useNavigate();
  const { t: tLanding, translateData, translateNarrative, language } = useTranslation();
  
  // Fonction pour traduire avec Google Translate (gratuit)
  const translateWithGoogle = async (text, fromLang = 'fr', toLang = 'en') => {
    if (!text || typeof text !== 'string' || fromLang === toLang) return text;
    
    try {
      const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang}&tl=${toLang}&dt=t&q=${encodeURIComponent(text)}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data && data[0] && Array.isArray(data[0])) {
        return data[0].map((item) => item[0]).join('');
      }
      return text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  };

  const [view, setView] = useState("flow");
  const [trendSort, setTrendSort] = useState("last");
  const [chartMode, setChartMode] = useState("heatmap");
  const [selectedDate, setSelectedDate] = useState(null); // Date sélectionnée (format YYYY-MM-DD)
  const [trendFromApi, setTrendFromApi] = useState(null); // Données tendance (6 mois) depuis l'API
  const [trendDataLoading, setTrendDataLoading] = useState(false);
  const [trendDataError, setTrendDataError] = useState(null);
  const [trendRetryCount, setTrendRetryCount] = useState(0);
  const [availableDates, setAvailableDates] = useState([]); // Liste des dates disponibles depuis l'API
  const isInitialized = useRef(false); // Pour éviter la boucle infinie lors de l'initialisation

  const [worldGeo, setWorldGeo] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [marketFlowData, setMarketFlowData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);

  useEffect(() => {
    runSelfTests();
  }, []);

  // Load market flow data from API - se déclenche au montage et quand selectedDate change
  useEffect(() => {
    let mounted = true;
    setDataLoading(true);
    setDataError(null);

    // Use Constants for API URL, but detect localhost for development
    // Note: En développement, utilisez l'URL de production car Gunicorn écoute sur un socket Unix
    // Pour tester en local, démarrez Django avec: python manage.py runserver 0.0.0.0:8000
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Utiliser la date sélectionnée si disponible, sinon charger la date la plus récente (par défaut)
    const dateParam = selectedDate ? `date=${selectedDate}` : '';
    const apiUrl = `${Constants.BACKEND_SERVER_BASE_URL}/capitalflow/data${dateParam ? '?' + dateParam : ''}`;

    console.log('🔄 Loading market flow data from API:', apiUrl);
    console.log('📍 Current location:', window.location.href);
    console.log('📅 Selected date:', selectedDate || 'latest');
    console.log('📋 Available dates (before fetch):', availableDates);

    fetch(apiUrl)
      .then((r) => {
        console.log('📡 Response status:', r.status, r.statusText);
        if (!r.ok) {
          return r.text().then(text => {
            console.error('❌ API error response:', text);
            throw new Error(`API fetch failed: ${r.status} - ${text.substring(0, 100)}`);
          });
        }
        return r.json();
      })
      .then((data) => {
        if (!mounted) return;
        // Log tous les champs disponibles pour debug
        console.log('✅ Market flow data loaded from API - All fields:', Object.keys(data));
        console.log('✅ Market flow data loaded from API:', {
          asOf: data.asOf,
          regionsCount: data.regions?.length || 0,
          sectorsCount: data.sectorRotation?.length || 0,
          hasData: !!data,
          availableDatesCount: data.availableDates?.length || 0,
          momentumNarrative: data.momentumNarrative ? data.momentumNarrative.substring(0, 100) + '...' : 'null ou undefined',
          momentumNarrativeLength: data.momentumNarrative?.length || 0,
          // Vérifier d'autres champs possibles
          marketCommentary: data.marketCommentary ? data.marketCommentary.substring(0, 100) + '...' : 'null',
          commentary: data.commentary ? data.commentary.substring(0, 100) + '...' : 'null',
          narrative: data.narrative ? data.narrative.substring(0, 100) + '...' : 'null',
          marketRegime: data.marketRegime ? data.marketRegime.substring(0, 100) + '...' : 'null',
        });
        // Log le texte complet de momentumNarrative
        console.log('📝 Full momentumNarrative from API:', data.momentumNarrative);
        console.log('📊 Full API response:', JSON.stringify(data, null, 2));
        
        // Mettre à jour la liste des dates disponibles
        console.log('📅 API returned availableDates:', data.availableDates);
        if (data.availableDates && Array.isArray(data.availableDates) && data.availableDates.length > 0) {
          console.log('✅ Setting availableDates:', data.availableDates);
          setAvailableDates(data.availableDates);
          // Initialiser selectedDate seulement une fois au premier chargement
          if (!isInitialized.current && !selectedDate && data.availableDates.length > 0) {
            console.log('🎯 Initializing selectedDate to:', data.availableDates[0]);
            isInitialized.current = true;
            setSelectedDate(data.availableDates[0]);
          }
        } else {
          console.warn('⚠️ No availableDates in API response or empty array');
        }
        if (data && data.asOf) {
          // Transformer les données de l'API en structure attendue par le composant
          const transformedData = {
            asOf: data.asOf,
            // Essayer plusieurs champs possibles pour le texte du Market Regime
            momentumNarrative: data.momentumNarrative || data.marketCommentary || data.commentary || data.narrative || data.marketRegime || null,
            // Garder les données brutes des pays pour TableView (avant transformation pour la carte)
            countriesRaw: data.countries || [],  // Pays bruts de l'API pour TableView
            regions: data.regions || [],  // Régions géographiques
            sectorRotation: (data.sectorRotation || []).map(s => ({
              name: s.sector,
              ret: s.value || 0,  // CNF pour remplacer return
              deltaBp: (s.changePct || 0),  // NFP est déjà en % (pas besoin de multiplier par 100)
              investorComment: s.investorComment || null,
            })),
            // Utiliser les valeurs headline depuis l'API Capital_flow (cnf et nfp)
            headline: {
              cnf: data.headline?.cnf || 0,  // CNF en %
              nfp: data.headline?.nfp || 0,  // NFP en %
            },
            // Transformer countries pour la carte (utiliser data.countries)
            // CountryMap attend: { iso3, ret, lat, lon, cnfCum3m, nfpPct } - ret contiendra CNF
            countries: (data.countries || [])
              .filter(c => c.coordinates && Array.isArray(c.coordinates) && c.coordinates.length >= 2)
              .map(c => ({
                iso3: c.iso3 || c.country,  // Utiliser iso3 si disponible (pour GeoJSON), sinon country
                ret: c.value || 0,  // CNF pour remplacer return - CountryMap utilise 'ret' pour la valeur
                lat: c.coordinates[0],  // latitude (premier élément)
                lon: c.coordinates[1],  // longitude (deuxième élément)
                value: c.value || 0,  // Garder aussi 'value' pour compatibilité (CNF)
                cnfCum3m: c.value || 0,  // CNF_cum_3M (value est cnf_cum_3m depuis l'API)
                nfpPct: c.changePct || 0,  // NFP_pct (changePct est nfp_pct depuis l'API)
                coordinates: c.coordinates,  // Garder aussi coordinates
              })),
            // Transformer styles pour InvestingStyles (attend: name, share, deltaBp)
            styles: (data.styles || []).map(s => {
              const total = (data.styles || []).reduce((sum, st) => sum + Math.abs(st.value || 0), 0);
              const share = total > 0 ? ((s.value || 0) / total) * 100 : 0;
              return {
                name: s.style,
                share: share,
                deltaBp: (s.changePct || 0),  // NFP est déjà en % (pas besoin de multiplier par 100)
                ret: s.value || 0,  // CNF pour remplacer return
                investorComment: s.investorComment || null,
              };
            }),
            // Transformer marketCaps pour KpiCards (attend: title, val, tag, delta)
            // Ordre selon l'image: Mega Cap, Large Cap, Mid Cap, Nano Cap, Small Cap
            marketCaps: (() => {
              const order = ['Mega Cap', 'Large Cap', 'Mid Cap', 'Nano Cap', 'Small Cap'];
              const mapped = (data.marketCaps || []).map(m => ({
                title: m.mktClass,
                val: m.value || 0,  // CNF pour remplacer return
                tag: m.mktClass,
                delta: (m.changePct || 0),  // NFP est déjà en % (pas besoin de multiplier par 100)
                investorComment: m.investorComment || null,
                order: order.indexOf(m.mktClass) >= 0 ? order.indexOf(m.mktClass) : 999,
              }));
              return mapped.sort((a, b) => a.order - b.order).map(({ order, ...rest }) => rest);
            })(),
            // Transformer fundamentals pour KpiCards (attend: title, val, tag, delta)
            // Ordre logique: Excellent, Good, Average, Bad, Worst
            fundamentals: (() => {
              const order = ['Excellent', 'Good', 'Average', 'Bad', 'Worst'];
              const mapped = (data.fundamentals || []).map(f => ({
                title: f.fScore,
                val: f.value || 0,  // CNF pour remplacer return
                tag: f.fScore,
                delta: (f.changePct || 0),  // NFP est déjà en % (pas besoin de multiplier par 100)
                investorComment: f.investorComment || null,
                order: order.indexOf(f.fScore) >= 0 ? order.indexOf(f.fScore) : 999,
              }));
              return mapped.sort((a, b) => a.order - b.order).map(({ order, ...rest }) => rest);
            })(),
          };
          setMarketFlowData(transformedData);
        } else {
          console.warn('⚠️ API returned empty or invalid data:', data);
          setDataError('API returned empty data');
          setMarketFlowData(null);
        }
      })
      .catch((e) => {
        if (!mounted) return;
        console.error('❌ Failed to load market flow data from API:', e);
        console.error('Error details:', {
          message: e.message,
          stack: e.stack,
        });
        setDataError(e?.message || "Failed to load market flow data");
        setMarketFlowData(null);
      })
      .finally(() => {
        if (mounted) {
          console.log('🏁 Fetch completed, setting loading to false');
          setDataLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [selectedDate]); // Recharger les données quand selectedDate change

  useEffect(() => {
    let mounted = true;
    setMapError(null);

    fetch(WORLD_GEO_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`Map fetch failed: ${r.status}`);
        return r.json();
      })
      .then((geojson) => {
        if (!mounted) return;
        setWorldGeo(geojson);
      })
      .catch((e) => {
        if (!mounted) return;
        setWorldGeo(null);
        setMapError(e?.message || "Map load error");
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Charger les données de tendance (6 mois) pour la vue Heatmap : relatives à la date sélectionnée
  useEffect(() => {
    if (!availableDates || availableDates.length === 0) {
      setTrendFromApi(null);
      return;
    }
    const datesToFetch = selectedDate
      ? availableDates
          .filter((d) => String(d).localeCompare(selectedDate) <= 0)
          .sort((a, b) => String(b).localeCompare(String(a)))
          .slice(0, 6)
      : availableDates.slice(0, 6);
    if (datesToFetch.length === 0) {
      setTrendFromApi(null);
      setTrendDataLoading(false);
      return;
    }
    let mounted = true;
    setTrendDataLoading(true);
    setTrendDataError(null);
    const baseUrl = Constants.BACKEND_SERVER_BASE_URL;
    Promise.all(
      datesToFetch.map((d) =>
        fetch(`${baseUrl}/capitalflow/data?date=${d}`).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
      )
    )
      .then((responses) => {
        if (!mounted) return;
        const locale = language === "en" ? "en-US" : "fr-FR";
        const monthLabels = datesToFetch.map((d) => {
          try {
            return new Date(d + "T00:00:00").toLocaleDateString(locale, { month: "short" });
          } catch (e) {
            return d;
          }
        });
        const getItems = (res, key) => res[key] || [];
        const buildRows = (getItemKey, getName, getValue) => {
          const nameSet = new Set();
          responses.forEach((res) =>
            (getItems(res, getItemKey) || []).forEach((item) => nameSet.add(getName(item)))
          );
          return Array.from(nameSet).map((name, i) => ({
            id: `row-${getItemKey}-${i}-${String(name).slice(0, 20)}`,
            name,
            points: responses.map((res, idx) => {
              const item = (getItems(res, getItemKey) || []).find((x) => getName(x) === name);
              return { m: monthLabels[idx] || "", v: item != null ? Number(getValue(item)) || 0 : 0 };
            }),
          }));
        };
        setTrendFromApi({
          regions: buildRows("regions", (r) => r.region, (r) => r.value),
          countries: buildRows("countries", (c) => c.iso3 || c.country, (c) => c.value),
          sectors: buildRows("sectorRotation", (s) => s.sector || s.name, (s) => s.value),
          marketCaps: buildRows("marketCaps", (m) => m.mktClass || m.title, (m) => m.value),
          fundamentals: buildRows("fundamentals", (f) => f.fScore || f.title, (f) => f.value),
          styles: buildRows("styles", (s) => s.style || s.name, (s) => s.value),
        });
      })
      .catch((e) => {
        if (mounted) {
          setTrendDataError(e?.message || "Failed to load trend data");
          setTrendFromApi(null);
        }
      })
      .finally(() => {
        if (mounted) setTrendDataLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [availableDates, selectedDate, language, trendRetryCount]);

  // Use API data only (or empty object as fallback for hooks)
  // Ensure headline always exists with default values
  const data = marketFlowData 
    ? { 
        ...marketFlowData, 
        headline: marketFlowData.headline || { cnf: 0, nfp: 0 },
        regions: marketFlowData.regions || [],  // Régions géographiques pour TableView
        countriesRaw: marketFlowData.countriesRaw || [],  // Pays bruts pour TableView
        countries: marketFlowData.countries || [],  // Pays transformés pour la carte
        sectorRotation: marketFlowData.sectorRotation || [],  // Secteurs pour TableView
        styles: marketFlowData.styles || [],
        marketCaps: marketFlowData.marketCaps || [],
        fundamentals: marketFlowData.fundamentals || [],
        momentumNarrative: marketFlowData.momentumNarrative || null,  // Narrative text from API
      }
    : { 
        asOf: new Date().toISOString(), 
        headline: { cnf: 0, nfp: 0 }, 
        regions: [], 
        countriesRaw: [],
        sectorRotation: [], 
        countries: [], 
        styles: [], 
        marketCaps: [], 
        fundamentals: [],
        momentumNarrative: null,
      };

  // Utiliser les dates réelles disponibles depuis l'API
  // All hooks must be called before any conditional returns
  const selectedAsOfLabel = useMemo(() => {
    if (!selectedDate) return '';
    const date = new Date(selectedDate + 'T00:00:00');
    const locale = language === 'en' ? 'en-US' : 'fr-FR';
    return date.toLocaleDateString(locale, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate, language]);

  const monthOptions = useMemo(() => {
    console.log('🔍 Generating monthOptions from availableDates:', availableDates);
    if (!availableDates || availableDates.length === 0) {
      console.warn('⚠️ No availableDates, returning empty array');
      return [];
    }
    const locale = language === 'en' ? 'en-US' : 'fr-FR';
    // Créer les options à partir des dates réelles disponibles
    const options = availableDates.map((dateStr) => {
      try {
        const date = new Date(dateStr + 'T00:00:00');
        if (isNaN(date.getTime())) {
          console.error('❌ Invalid date:', dateStr);
          return null;
        }
        const label = date.toLocaleDateString(locale, { 
          month: "short", 
          year: "numeric" 
        });
        return { 
          date: dateStr, 
          label 
        };
      } catch (e) {
        console.error('❌ Error parsing date:', dateStr, e);
        return null;
      }
    }).filter(Boolean); // Filtrer les valeurs null
    
    console.log('✅ Generated monthOptions:', options);
    return options;
  }, [availableDates, language]);

  // Transformer les régions géographiques (pas les pays) de l'API en format attendu par RegionRows
  const regionRows = useMemo(() => {
    // Utiliser data.regions (régions géographiques) et non data.countries (pays)
    const regions = data.regions || [];
    if (!Array.isArray(regions)) return [];
    return regions.map((r, index) => {
      const value = r.value || 0;  // CNF
      const changePct = r.changePct || 0;  // NFP (déjà en %)
      const deltaBp = changePct;  // NFP est déjà en %, pas besoin de multiplier par 100
      
      // Déterminer la force basée sur la VRAIE valeur (pas de seuils arbitraires)
      // Si la valeur est positive, c'est un inflow (strong ou moderate selon l'intensité)
      // Si la valeur est négative, c'est un outflow
      let strength = 'moderate';
      if (value >= 5) {
        strength = 'strong';  // Fort inflow
      } else if (value >= 0) {
        strength = 'moderate'; // Inflow modéré
      } else {
        strength = 'outflow'; // Outflow (négatif)
      }
      
      // Direction basée sur le delta (changePct / NFP)
      let dir = changePct >= 0 ? 'up' : 'down';
      
      return {
        key: r.region || `region-${index}`, // Utiliser region comme key (régions géographiques)
        ret: value,
        delta: deltaBp,
        strength: strength,
        dir: dir,
        investorComment: r.investorComment || null,
      };
      });
    }, [data.regions]);
 
  // Mois pour la vue Heatmap : 6 mois relatifs à la date sélectionnée (comme le fetch trend)
  const trendMonths = useMemo(() => {
    const locale = language === "en" ? "en-US" : "fr-FR";
    if (availableDates && availableDates.length > 0) {
      const last6 = selectedDate
        ? availableDates
            .filter((d) => String(d).localeCompare(selectedDate) <= 0)
            .sort((a, b) => String(b).localeCompare(String(a)))
            .slice(0, 6)
        : availableDates.slice(0, 6);
      return last6.map((d) => {
        try {
          const date = new Date(d + "T00:00:00");
          const label = date.toLocaleDateString(locale, { month: "short" });
          return { key: d, label };
        } catch (e) {
          return { key: d, label: d };
        }
      });
    }
    const end = selectedDate ? new Date(selectedDate + "T00:00:00") : new Date();
    const out = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(end);
      d.setMonth(d.getMonth() - i);
      out.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString(locale, { month: "short" }),
      });
    }
    return out;
  }, [availableDates, selectedDate, language]);

  // Données tendance pour la vue Heatmap : priorité aux données API (6 mois réels), sinon fallback snapshot répété
  const trend = useMemo(() => {
    if (trendFromApi && (trendFromApi.regions?.length > 0 || trendFromApi.sectors?.length > 0)) {
      return trendFromApi;
    }
    const months = trendMonths;
    const point = (v) => months.map((m) => ({ m: m.label, v: Number(v) ?? 0 }));
    const regions = (data.regions || []).map((r, i) => ({
      id: `region-${i}`,
      name: r.region || r.key || `Region ${i}`,
      points: point(r.value ?? r.ret ?? 0),
    }));
    const countries = (data.countries || []).slice(0, 20).map((c, i) => ({
      id: `country-${i}`,
      name: c.iso3 || c.country || `C${i}`,
      points: point(c.ret ?? c.value ?? 0),
    }));
    const sectors = (data.sectorRotation || []).map((s, i) => ({
      id: `sector-${i}`,
      name: s.name || s.sector || `Sector ${i}`,
      points: point(s.ret ?? 0),
    }));
    const marketCaps = (data.marketCaps || []).map((m, i) => ({
      id: `mcap-${i}`,
      name: m.title || `M${i}`,
      points: point(m.val ?? 0),
    }));
    const fundamentals = (data.fundamentals || []).map((f, i) => ({
      id: `fund-${i}`,
      name: f.title || `F${i}`,
      points: point(f.val ?? 0),
    }));
    const styles = (data.styles || []).map((s, i) => ({
      id: `style-${i}`,
      name: s.name || `Style ${i}`,
      points: point(s.ret ?? s.share ?? 0),
    }));
    return { regions, countries, sectors, marketCaps, fundamentals, styles };
  }, [trendFromApi, data.regions, data.countries, data.sectorRotation, data.marketCaps, data.fundamentals, data.styles, trendMonths]);

  // Pour la vue Heatmap : mois et données du plus ancien au plus récent (plus récent à droite)
  const trendMonthsOldestFirst = useMemo(() => [...(trendMonths || [])].reverse(), [trendMonths]);
  const trendOldestFirst = useMemo(() => {
    if (!trend || !trend.regions) return trend;
    const rev = (arr) => (arr || []).map((r) => ({ ...r, points: [...(r.points || [])].reverse() }));
    return {
      regions: rev(trend.regions),
      countries: rev(trend.countries),
      sectors: rev(trend.sectors),
      marketCaps: rev(trend.marketCaps),
      fundamentals: rev(trend.fundamentals),
      styles: rev(trend.styles),
    };
  }, [trend]);

  const [isNarrativeExpanded, setIsNarrativeExpanded] = useState(false);
  const [translatedNarrative, setTranslatedNarrative] = useState(null);
  
  // Drill-down modal state
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillCtx, setDrillCtx] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  // Fonction pour ouvrir le modal drill-down
  const openDrill = async (scopeType, scopeValue) => {
    setDrillLoading(true);
    setDrillOpen(true);
    
    // Récupérer les valeurs agrégées depuis la page principale (CNF et NFP)
    let scopeCnf = 0;
    let scopeNfp = 0;
    
    if (data) {
      if (scopeType === 'Region') {
        const region = (data.regions || []).find(r => (r.region || r.key) === scopeValue);
        if (region) {
          scopeCnf = region.value || 0;  // CNF
          scopeNfp = (region.changePct || 0);  // NFP est déjà en %
        }
      } else if (scopeType === 'Country') {
        const countriesData = data.countriesRaw || data.countries || [];
        const country = countriesData.find(c => (c.country || c.iso3 || c.code) === scopeValue);
        if (country) {
          scopeCnf = country.value || country.ret || 0;  // CNF
          scopeNfp = (country.changePct || 0);  // NFP est déjà en %
        }
      } else if (scopeType === 'Sector') {
        const sector = (data.sectorRotation || []).find(s => (s.name || s.sector) === scopeValue);
        if (sector) {
          scopeCnf = sector.ret || sector.value || 0;  // CNF
          scopeNfp = sector.deltaBp || (sector.changePct || 0);  // NFP est déjà en %
        }
      } else if (scopeType === 'MarketCap') {
        const marketCap = (data.marketCaps || []).find(k => k.title === scopeValue);
        if (marketCap) {
          scopeCnf = marketCap.val || 0;  // CNF
          scopeNfp = marketCap.delta || 0;  // NFP est déjà en %
        }
      } else if (scopeType === 'Fundamental') {
        const fundamental = (data.fundamentals || []).find(k => k.title === scopeValue);
        if (fundamental) {
          scopeCnf = fundamental.val || 0;  // CNF
          scopeNfp = fundamental.delta || 0;  // NFP est déjà en %
        }
      } else if (scopeType === 'InvestingStyle') {
        const style = (data.styles || []).find(s => s.name === scopeValue);
        if (style) {
          scopeCnf = style.ret || 0;  // CNF
          scopeNfp = style.deltaBp || 0;  // NFP est déjà en %
        }
      }
    }
    
    // Créer le contexte initial avec les valeurs de la page principale (CNF et NFP)
    const ctx = {
      scopeType,
      scopeValue,
      asOfLabel: selectedAsOfLabel,
      scopeCnf,
      scopeNfp,
    };
    setDrillCtx(ctx);
    
    try {
      // Récupérer les données depuis l'API Capital_flow_Top200
      // Toujours passer la date sélectionnée pour avoir les données du bon mois
      const dateParam = selectedDate ? `&date=${selectedDate}` : '';
      const url = `${Constants.BACKEND_SERVER_BASE_URL}/capitalflow/drilldown?scopeType=${encodeURIComponent(scopeType)}&scopeValue=${encodeURIComponent(scopeValue)}${dateParam}`;
      console.log('🔍 Opening drill-down with:', { scopeType, scopeValue, selectedDate, url });
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`API fetch failed: ${response.status}`);
      }
      
      const apiData = await response.json();
      
      // Utiliser les valeurs de la page principale pour les KPIs du scope, pas celles de l'API
      setDrillCtx(ctx);
      setDrillData({
        cnf: scopeCnf,  // CNF depuis la page principale
        nfp: scopeNfp !== null && scopeNfp !== undefined ? scopeNfp : null,  // NFP depuis la page principale
        topAssets: (apiData.topAssets || []).map(asset => ({
          asset: asset.asset || '',
          region: asset.region || '',
          country: asset.country || '',
          sector: asset.sector || '',
          style: asset.style || '',
          marketCap: asset.marketCap || '',
          fundamental: asset.fundamental || '',
          cnfPct: asset.cnfPct || 0,  // CNF depuis Capital_flow_Top200
          nfpPct: asset.nfpPct || 0,  // NFP depuis Capital_flow_Top200
        })),
      });
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      setDrillData({
        cnf: scopeCnf,  // Utiliser les valeurs de la page principale même en cas d'erreur
        nfp: scopeNfp !== null && scopeNfp !== undefined ? scopeNfp : null,
        topAssets: [],
      });
    } finally {
      setDrillLoading(false);
    }
  };

  const closeDrill = () => {
    setDrillOpen(false);
    setDrillCtx(null);
    setDrillData(null);
  };

  // Traduire automatiquement le texte de la narrative en anglais avec Google Translate
  useEffect(() => {
    if (language === 'en' && data?.momentumNarrative) {
      translateWithGoogle(data.momentumNarrative, 'fr', 'en').then((translated) => {
        setTranslatedNarrative(translated);
      });
    } else {
      setTranslatedNarrative(null);
    }
  }, [language, data?.momentumNarrative]);

  // Show loading state (after all hooks) - only check dataLoading, not marketFlowData
  if (dataLoading) {
  return (
      <div className="td-wrap">
        <Style />
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>{tLanding('loadingMarketData')}</p>
            </div>
            </div>
    );
  }

  // Show error state (after all hooks) - only if we have an error and no data
  if (dataError && !marketFlowData) {
    return (
      <div className="td-wrap">
        <Style />
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <h2 style={{ color: "#d32f2f", marginBottom: "1rem" }}>
            {tLanding('errorLoadingData')}
          </h2>
          <p style={{ color: "#666", marginBottom: "1rem" }}>
            {tLanding('errorLoadingApi')}
          </p>
          {dataError && (
            <p style={{ color: "#999", fontSize: "0.9rem" }}>
              {tLanding('errorLoadingData')}: {dataError}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: "1rem",
              padding: "0.5rem 1rem",
              backgroundColor: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {tLanding('retry')}
          </button>
            </div>
            </div>
    );
  }

  return (
    <TranslationProvider>
      <div className="td-wrap">
        <Style />

      <Header
        asOf={selectedAsOfLabel}
        view={view}
        headline={data.headline}
        setView={setView}
      />

      <div className="td-toolbar">
        <select
          key={`month-select-${language}`}
          className="td-select"
          value={selectedDate || ''}
          onChange={(e) => setSelectedDate(e.target.value)}
          aria-label="Month selector"
        >
          {monthOptions.length === 0 ? (
            <option value="">{tLanding('loadingMarketData')}</option>
          ) : (
            monthOptions.map((o) => (
              <option key={`${o.date}-${language}`} value={o.date}>
                {o.label}
              </option>
            ))
          )}
        </select>

        <p className="td-toolbarHint">
          {view === "chart" ? "📈 " + tLanding('trendFollowing') : "🔎 " + tLanding('historicalView')}
        </p>

        {view === "chart" ? (
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", borderRadius: 12, overflow: "hidden", border: "1px solid rgba(30,40,34,.12)", background: "#fff" }}>
              <button
                type="button"
                onClick={() => setChartMode("lines")}
                style={{
                  padding: "10px 12px",
                  fontWeight: 950,
                  fontSize: 12,
                  border: 0,
                  cursor: "pointer",
                  background: chartMode === "lines" ? "rgba(181,154,91,.18)" : "transparent",
                  color: chartMode === "lines" ? "var(--ink)" : "var(--muted)",
                }}
              >
                {tLanding('linesMode')}
              </button>
              <button
                type="button"
                onClick={() => setChartMode("heatmap")}
                style={{
                  padding: "10px 12px",
                  fontWeight: 950,
                  fontSize: 12,
                  border: 0,
                  cursor: "pointer",
                  background: chartMode === "heatmap" ? "rgba(181,154,91,.18)" : "transparent",
                  color: chartMode === "heatmap" ? "var(--ink)" : "var(--muted)",
                }}
              >
                {tLanding('heatmapMode')}
              </button>
            </div>
            <select
              className="td-select"
              value={trendSort}
              onChange={(e) => setTrendSort(e.target.value)}
              aria-label={tLanding('sortedBySortKey')}
              style={{ marginBottom: 0 }}
            >
              <option value="last">{tLanding('sortLast')}</option>
              <option value="delta1m">{tLanding('sortDelta1m')}</option>
              <option value="slope">{tLanding('sortSlope')}</option>
            </select>
          </div>
        ) : null}
      </div>

      {view === "flow" ? (
        <>
          <div className="td-grid">
            <div className="td-col">
              <Card title={tLanding('regionFlows')} subtitle={tLanding('capitalFlowSubtitle')}>
                <RegionRows
                  rows={regionRows}
                  onClick={(region) => openDrill('Region', region)}
                />
              </Card>

              <Card title={tLanding('sectorRotation')} subtitle={tLanding('capitalFlowSubtitle')}>
                <SectorRotationChart rows={data.sectorRotation} onDrill={(sector) => openDrill('Sector', sector)} />
              </Card>
            </div>

            <div className="td-col">
              <Card title={tLanding('countryPerformance')} subtitle={tLanding('capitalFlowSubtitle')}>
                {mapError ? (
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(30,40,34,.12)",
                      background: "rgba(181,154,91,.08)",
                      color: "#4a3d22",
                      fontWeight: 800,
                      marginBottom: 10,
                    }}
                  >
                    Map unavailable: {mapError}
            </div>
                ) : null}

                <CountryMap
                  worldGeo={worldGeo}
                  countries={data.countries}
                  onClick={(iso3) => {
                    // Trouver le pays depuis les données
                    const country = data.countriesRaw?.find(c => (c.iso3 === iso3 || c.iso3 === iso3.toUpperCase() || c.iso3 === iso3.toLowerCase()));
                    // Le backend attend le code ISO2 dans la colonne Market
                    // country?.country contient le code ISO2 (ex: "ZA"), iso3 contient le code ISO3 (ex: "ZAF")
                    // Le backend convertit automatiquement ISO3 en ISO2, mais on peut aussi envoyer directement ISO2
                    const countryCode = country?.country || iso3;
                    openDrill('Country', countryCode);
                  }}
                />

                <div className="td-kpiBar">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{tLanding('cnfLabel')}</span>
                  <span>
                      <b>{fmtPct(data.headline.cnf || 0)}</b>
                  </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>{tLanding('nfpLabel')}</span>
                  <span>
                      <b className="td-gold">{fmtPct(data.headline.nfp || 0)}</b>
                  </span>
                  </div>
                </div>
              </Card>

              <Card title={tLanding('marketCapPerformance')} subtitle={tLanding('capitalFlowSubtitle')}>
                <KpiCards items={data.marketCaps} onItemClick={(title) => openDrill('MarketCap', title)} />
              </Card>

              <Card title={tLanding('fundamentalScore')} subtitle={tLanding('capitalFlowSubtitle')}>
                <KpiCards items={data.fundamentals} onItemClick={(title) => openDrill('Fundamental', title)} />
              </Card>

              <Card title={tLanding('investingStyles')} subtitle={tLanding('capitalFlowSubtitle')}>
                <InvestingStyles rows={data.styles} onDrill={(name) => openDrill('InvestingStyle', name)} />
              </Card>
            </div>
          </div>

          {/* Full width market commentary at the very bottom */}
          <div style={{ marginTop: 28 }}>
            <div
              style={{
                width: "100%",
                background: "#ffffff",
                border: "1px solid rgba(30,40,34,.12)",
                borderRadius: 18,
                padding: "22px 26px",
                boxShadow: "0 8px 22px rgba(16,24,20,.08)",
                color: "var(--ink)",
                fontSize: 14,
                lineHeight: 1.6,
                fontWeight: 600,
              }}
            >
              <div style={{ fontWeight: 950, marginBottom: 10 }}>
                {tLanding('marketRegime')} – {selectedAsOfLabel || tLanding('loadingMarketData')}
              </div>
              <div
                className={`td-narrative-text ${isNarrativeExpanded ? 'expanded' : ''}`}
                style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}
              >
                {data.momentumNarrative ? (
                  language === 'en' && translatedNarrative 
                    ? translatedNarrative 
                    : translateNarrative(data.momentumNarrative)
                ) : tLanding('noDataAvailable')}
              </div>
              {data.momentumNarrative && (
                <button
                  onClick={() => setIsNarrativeExpanded(!isNarrativeExpanded)}
                  className="td-seeMoreBtn"
                  style={{
                    marginTop: 12,
                    padding: "8px 16px",
                    background: "rgba(181,154,91,.12)",
                    border: "1px solid rgba(181,154,91,.25)",
                    borderRadius: 8,
                    color: "#4a3d22",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  {isNarrativeExpanded ? tLanding('seeLess') : tLanding('seeMore')}
                </button>
              )}
            </div>
          </div>
        </>
      ) : view === "table" ? (
        <TableView data={data} onDrill={openDrill} />
      ) : (
        <>
          {trendDataLoading && (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--muted)", fontWeight: 800 }}>
              {tLanding("loadingMarketData")} (6 mois)
            </div>
          )}
          {trendDataError && !trendDataLoading && (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <p style={{ color: "#c6523f", marginBottom: "1rem" }}>{trendDataError}</p>
              <button
                type="button"
                className="td-primaryBtn"
                onClick={() => { setTrendDataError(null); setTrendRetryCount((c) => c + 1); }}
              >
                {tLanding("retry")}
              </button>
            </div>
          )}
          {!trendDataLoading && !trendDataError && (
            <MarketFlowChartView
              trend={trendOldestFirst}
              months={trendMonthsOldestFirst}
              sortKey={trendSort}
              setTrendSort={setTrendSort}
              chartMode={chartMode}
              setChartMode={setChartMode}
              t={tLanding}
              translateData={translateData}
            />
          )}
        </>
      )}

      <DrillDownModal 
        open={drillOpen} 
        ctx={drillCtx} 
        data={drillData} 
        loading={drillLoading}
        onClose={closeDrill}
        onSelectAsset={(asset) => {
          if (asset) {
            navigateToStockDashboard(navigate, asset, "marketflow");
            closeDrill();
          }
        }}
      />
      </div>
    </TranslationProvider>
  );
}

/* ---------------- Header (hero only — nav global dans Layout) ---------------- */
function Header({ asOf, view, setView, headline }) {
  const { t } = useTranslation();
  return (
    <MarketPageHero
      title={t("marketFlows")}
      asOf={`${t("dataAsOf")} ${asOf}`}
      pills={[
        { label: t("cnfLabel"), value: fmtPct(headline.cnf || 0) },
        { label: t("nfpLabel"), value: fmtPct(headline.nfp || 0), gold: true },
      ]}
      view={view}
      setView={setView}
      heatmapEnabled
      viewLabels={{
        flow: t("switchToFlowView"),
        table: t("switchToTableView"),
        chart: t("switchToHeatmapView"),
      }}
    />
  );
}

/* ---------------- Reusable Card ---------------- */
function Card({ title, subtitle, children }) {
  return (
    <div className="td-card">
      <div className="td-cardHead">
        <div className="td-cardTitle">
          <h2>{title}</h2>
          {subtitle ? <div className="td-cardSub">{subtitle}</div> : null}
        </div>
      </div>
      {children}
            </div>
  );
}

/* ---------------- Region List ---------------- */
function RegionRows({ rows, onClick }) {
  const { translateData, t } = useTranslation();
  
  return (
    <div className="td-rows">
      <div className="td-rowHeader">
        <div className="td-rowHeaderLabel"></div>
        <div className="td-rowHeaderVal">{t('cnfLabel')}</div>
        <div className="td-rowHeaderPill">{t('nfpLabel')}</div>
      </div>
      {rows.map((r, index) => {
        const ret = r.ret || 0; // CNF_cum_3M
        const delta = r.delta || 0; // NFP_pct
        const regionName = translateData(r.key || 'N/A');
        
        // Déterminer la couleur selon les règles Market Flow
        const { color: textColor } = getMarketFlowCategory(ret, delta);
        
        return (
          <DrillRowTip
            key={r.key || `region-${index}`}
            comment={r.investorComment}
            className="td-row-wrap"
            style={{
              background: `linear-gradient(90deg, ${textColor}15, transparent 65%)`,
            }}
            onClick={() => onClick?.(r.key)}
          >
            <div className="td-row">
            <div className="td-rowLabel">{regionName}</div>
            <div className="td-rowVal" style={{ color: textColor }}>{fmtPct(ret)}</div>
            <div className="td-rowPill" style={{ color: textColor }}>{fmtPct(delta)}</div>
            </div>
          </DrillRowTip>
        );
      })}
    </div>
  );
}

/* ---------------- Sector Chart (stacked, no demo UI) ---------------- */
function DeltaBpLabel({ x, y, value }) {
  const v = Number(value ?? 0);
  // Pour Market Flow, tout est en noir et en pourcentages (NFP)
  const fill = "var(--ink)"; // Noir pour Market Flow
  const fontSize = 12;
  const tx = Number(x ?? 0) + 15;
  // Centrer verticalement: y est le centre de la barre, ajuster pour la baseline du texte
  // En SVG, y positionne la baseline, donc on ajuste de ~0.35 * fontSize pour centrer
  const ty = Number(y ?? 0) + 15;
  return (
    <text 
      x={tx} 
      y={ty} 
      fontSize={fontSize} 
      fontWeight={900} 
      fill={fill} 
      style={{ fontVariantNumeric: "tabular-nums" }}
      dominantBaseline="middle"
    >
      {fmtPct(v)}
    </text>
  );
}

// Hook pour détecter la taille de l'écran
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return windowSize;
}

function SectorRotationChart({ rows, onDrill }) {
  const { width } = useWindowSize();
  const { t, translateData } = useTranslation();
  
  // Hauteur adaptative selon la taille de l'écran
  const chartHeight = useMemo(() => {
    if (width <= 480) return 350;
    if (width <= 768) return 400;
    if (width <= 1200) return 450;
    return 500;
  }, [width]);

  // Marges adaptatives
  const chartMargins = useMemo(() => {
    if (width <= 480) return { top: 6, right: 60, left: 10, bottom: 6 };
    if (width <= 768) return { top: 7, right: 60, left: 10, bottom: 7 };
    if (width <= 1200) return { top: 8, right: 40, left: 10, bottom: 8 };
    return { top: 8, right: 60, left: 10, bottom: 8 };
  }, [width]);

  // Largeur YAxis adaptative
  const yAxisWidth = useMemo(() => {
    if (width <= 480) return 75;
    if (width <= 768) return 90;
    if (width <= 1200) return 100;
    return 120;
  }, [width]);

  // Taille des ticks adaptative
  const tickFontSize = useMemo(() => {
    if (width <= 480) return 10;
    if (width <= 768) return 11;
    return 12;
  }, [width]);

  const base = useMemo(
    () =>
      rows.map((s) => ({
        name: s.name,
        ret: Number(s.ret) || 0,
        deltaBp: Number(s.deltaBp ?? 0) || 0,
      })),
    [rows]
  );

  const chartData = useMemo(() => {
    return [...base].sort((a, b) => (Number(b.ret) || 0) - (Number(a.ret) || 0));
  }, [base]);

  const allPositive = useMemo(() => chartData.every((d) => d.ret >= 0), [chartData]);

  const maxAbsDelta = useMemo(() => {
    const vals = chartData.map((d) => Math.abs(Number(d.deltaBp) || 0));
    return Math.max(1, ...vals);
  }, [chartData]);

  const maxAbsRet = useMemo(() => {
    const vals = chartData.map((d) => Math.abs(Number(d.ret) || 0));
    return Math.max(1, ...vals);
  }, [chartData]);

  const stackedData = useMemo(() => {
    // scale Δ (bp) into a %-ish space so it is visible when stacked.
    const scale = (maxAbsRet * 0.35) / maxAbsDelta;
    return chartData.map((d) => ({ ...d, deltaScaled: (Number(d.deltaBp) || 0) * scale }));
  }, [chartData, maxAbsDelta, maxAbsRet]);

  // Calculer la hauteur de chaque barre pour l'alignement
  const barHeight = 320 / chartData.length;
  const labelHeight = barHeight;

  // Taille de la légende adaptative
  const legendFontSize = useMemo(() => {
    if (width <= 480) return 10;
    if (width <= 768) return 11;
    return 12;
  }, [width]);

  const legendDotSize = useMemo(() => {
    if (width <= 480) return 10;
    return 12;
  }, [width]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: width <= 768 ? 12 : 16 }}>
      {/* Header pour les colonnes */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0 4px", marginBottom: "4px" }}>
        <div style={{ width: "40%" }}></div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "flex-end" }}>
          <span style={{ fontWeight: 800, color: "var(--muted)", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", width: "80px", textAlign: "center" }}>{t('cnfLabel')}</span>
          <span style={{ fontWeight: 800, color: "var(--muted)", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", width: "80px", textAlign: "center" }}>{t('nfpLabel')}</span>
          </div>
            </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {chartData.map((s, idx) => {
          const ret = s.ret || 0; // CNF_cum_3M
          const deltaBp = s.deltaBp || 0; // NFP_pct
          const sectorName = translateData(s.name || 'N/A');
          
          // Déterminer la couleur selon les règles Market Flow
          const { color: textColor } = getMarketFlowCategory(ret, deltaBp);
          
          return (
            <DrillRowTip
              key={s.name || `sector-${idx}`}
              comment={s.investorComment}
              asButton={false}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 14px",
                borderRadius: "14px",
                cursor: onDrill ? "pointer" : "default",
                background: `linear-gradient(90deg, ${textColor}15, transparent 65%)`,
                marginBottom: "4px",
              }}
              onClick={() => onDrill && onDrill(s.name)}
            >
              <span style={{ fontWeight: 900, fontSize: 14, color: "var(--ink)" }}>{translateData(s.name)}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span 
                  style={{ 
                    fontWeight: 950, 
                    fontSize: 14, 
                    color: textColor,
                    fontVariantNumeric: "tabular-nums",
                    width: "80px",
                    textAlign: "center"
                  }}
                >
                  {fmtPct(ret)}
                  </span>
                <span 
                  style={{ 
                    fontWeight: 900, 
                    fontSize: 13, 
                    color: textColor,
                    fontVariantNumeric: "tabular-nums",
                    width: "80px",
                    textAlign: "center"
                  }}
                >
                  {fmtPct(deltaBp)}
                </span>
            </div>
            </DrillRowTip>
          );
        })}
      </div>

      {/* Graphique avec barres HORIZONTALES - PLEINE LARGEUR */}
      <div className="td-chartBox" style={{ padding: "24px", width: "100%", minWidth: 0 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={stackedData} layout="vertical" margin={chartMargins}>
            <XAxis
              type="number"
              domain={allPositive ? [0, "dataMax"] : ["dataMin", "dataMax"]}
              tick={{ fontSize: tickFontSize }}
              tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
              axisLine={false}
            />
            <YAxis 
              type="category" 
              dataKey="name" 
              tick={{ fontSize: tickFontSize + 1, fontWeight: 800 }}
              width={yAxisWidth}
              axisLine={false}
            />
            <Tooltip
              formatter={(value, name, props) => {
                const p = props?.payload || {};
                if (name === "ret") return [fmtPct(Number(value)), "CNF"];
                if (name === "deltaScaled") return [fmtPct(Number(p.deltaBp ?? 0)), "NFP"];
                return [String(value), String(name)];
              }}
              labelFormatter={(label) => String(label)}
              contentStyle={{ borderRadius: 12, borderColor: "rgba(30,40,34,.12)" }}
            />

            <Bar dataKey="ret" stackId="a" radius={[0, 10, 10, 0]}>
              {stackedData.map((d, idx) => {
                const fillColor = colorByValue(d.ret, 'percentage');
                return <Cell key={idx} fill={fillColor} opacity={0.92} />;
              })}
            </Bar>

            <Bar dataKey="deltaScaled" stackId="a" radius={[0, 10, 10, 0]}>
              {stackedData.map((d, idx) => {
                const fillColor = colorByValue(d.deltaBp, 'percentage');
                return <Cell key={idx} fill={fillColor} opacity={0.55} />;
              })}
              <LabelList 
                dataKey="deltaBp" 
                content={(props) => <DeltaBpLabel {...props} />} 
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ---------------- Styles Radar ---------------- */
function InvestingStyles({ rows, onDrill }) {
  const { width } = useWindowSize();
  const { t, translateData } = useTranslation();
  
  // Hauteur adaptative pour le radar chart
  const radarHeight = useMemo(() => {
    if (width <= 480) return 200;
    if (width <= 768) return 220;
    if (width <= 1200) return 240;
    return 260;
  }, [width]);

  // Taille des ticks adaptative pour le radar
  const radarTickFontSize = useMemo(() => {
    if (width <= 480) return 9;
    if (width <= 768) return 10;
    return 11;
  }, [width]);

  // Tous les hooks doivent être appelés avant tout retour conditionnel
  const radarData = useMemo(() => {
    if (!rows || !Array.isArray(rows) || rows.length === 0) return [];
    return rows.map((s) => ({ 
      subject: translateData(s.name || 'N/A'), 
      value: s.share || 0 
    }));
  }, [rows, translateData]);

  // Retour conditionnel APRÈS tous les hooks
  if (!rows || !Array.isArray(rows) || rows.length === 0) {
    return <div style={{ padding: "1rem", color: "var(--muted)" }}>{t('noDataAvailable')}</div>;
  }

  return (
    <div className="td-styles">
      <div className="td-styleRows">
        <div className="td-styleHeader">
          <div className="td-styleHeaderLabel"></div>
          <div className="td-styleHeaderBar"></div>
          <div className="td-styleHeaderPct">{t('cnfLabel')}</div>
          <div className="td-styleHeaderDelta">{t('nfpLabel')}</div>
        </div>
        {rows.map((s, index) => {
          const share = s.share || 0;
          const deltaBp = s.deltaBp || 0;
          // Utiliser l'échelle absolue pour les basis points
          const barColor = colorByValue(deltaBp, 'basisPoints');
          const barGradientColor = lerpColor(barColor, "#ffffff", 0.2);
          
          const styleName = translateData(s.name || 'N/A');
          
          return (
            <DrillRowTip
              key={s.name || `style-${index}`}
              comment={s.investorComment}
              asButton={false}
              className="td-styleRow"
              style={{ cursor: onDrill ? "pointer" : "default" }}
              onClick={() => onDrill && onDrill(s.name)}
            >
              <span className="td-styleName">{styleName}</span>
              <div className="td-styleBar">
                <i 
                  style={{ 
                    width: `${Math.min(100, Math.max(0, share))}%`,
                    background: `linear-gradient(90deg, ${barColor}, ${barGradientColor})`
                  }} 
                />
          </div>
              <b className="td-stylePct">{Number(share).toFixed(1)}%</b>
              <span style={{ textAlign: "center", fontWeight: 900, fontSize: "13px", color: "var(--ink)" }}>
                {fmtPct(deltaBp)}
                  </span>
            </DrillRowTip>
          );
        })}
      </div>

      <div className="td-styleViz">
        <ResponsiveContainer width="100%" height={radarHeight}>
          <RadarChart data={radarData} outerRadius="75%">
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" tick={{ fontSize: radarTickFontSize }} />
            <PolarRadiusAxis tick={{ fontSize: radarTickFontSize - 1 }} />
            <Radar name="Styles" dataKey="value" stroke={tone.green} fill={tone.green2} fillOpacity={0.45} />
            <Tooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/* ---------------- KPI cards ---------------- */
function KpiCards({ items, onItemClick }) {
  const { t, translateData } = useTranslation();
  
  if (!items || !Array.isArray(items) || items.length === 0) {
    return <div style={{ padding: "1rem", color: "var(--muted)" }}>{t('noDataAvailable')}</div>;
  }
  
  return (
    <div className="td-kpis">
      {items.map((k, index) => {
        const val = k.val || 0;
        // Utiliser l'échelle absolue pour les pourcentages
        const bgColor = colorByValue(val, 'percentage');
        // Créer un gradient avec la couleur calculée
        const gradientColor = lerpColor(bgColor, "#ffffff", 0.15); // Légèrement plus clair pour le gradient
        const title = translateData(k.title || 'N/A');
        const tag = translateData(k.tag || '');
        
        return (
          <div 
            key={k.title || `kpi-${index}`} 
            className="td-kpi"
            style={{
              background: `linear-gradient(135deg, ${bgColor}, ${gradientColor})`,
              cursor: onItemClick ? 'pointer' : 'default',
            }}
            onClick={() => onItemClick && onItemClick(k.title)}
          >
            <div className="td-kpiTitle">{title}</div>
            <div className="td-kpiVal">{fmtPct(val)}</div>
            <div className="td-kpiSub">
              <span className="td-kpiTag">{tag}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 900, opacity: 0.95 }}>{fmtPct(k.delta || 0)}</span>
            </div>
          </div>
        );
      })}
      </div>
  );
}

/* ---------------- World Map ---------------- */
function CountryMap({ worldGeo, countries, onClick }) {
  const { t } = useTranslation();
  const [labelN, setLabelN] = useState("all");
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1 });

  const byIso3 = useMemo(() => {
    const m = new Map();
    countries.forEach((c) => m.set(String(c.iso3).toUpperCase(), c));
    return m;
  }, [countries]);

  const retRange = useMemo(() => {
    const vals = countries.map((c) => c.ret).filter((x) => Number.isFinite(x));
    const min = vals.length ? Math.min(...vals) : -10;
    const max = vals.length ? Math.max(...vals) : 10;
    const pad = (max - min) * 0.08;
    return { min: min - pad, max: max + pad };
  }, [countries]);

  const legendTicks = useMemo(() => {
    const { min, max } = retRange;
    const mid = (min + max) / 2;
    const q1 = min + (max - min) * 0.25;
    const q3 = min + (max - min) * 0.75;
    return [min, q1, mid, q3, max];
  }, [retRange]);

  // Tous les pays avec coordonnées valides (pour afficher les marqueurs)
  const allCountriesWithCoords = useMemo(() => {
    return countries
      .filter((c) => (c.cnfCum3m != null || c.ret != null) && Number.isFinite(c.lon) && Number.isFinite(c.lat))
      .map((c) => ({ ...c, lon: Number(c.lon), lat: Number(c.lat) }));
  }, [countries]);

  // Pays à labelliser (top/bottom N seulement, ou tous si labelN est "all")
  const labeledCountries = useMemo(() => {
    if (labelN === "all") {
      // Afficher tous les pays avec des labels
      return allCountriesWithCoords;
    }
    
    const sorted = [...allCountriesWithCoords].sort((a, b) => b.ret - a.ret);
    const n = Number(labelN) || 6;
    const top = sorted.slice(0, Math.max(0, n));
    const bottom = sorted.slice(Math.max(0, sorted.length - Math.max(0, n)));

    const seen = new Set();
    const merged = [];
    [...top, ...bottom].forEach((c) => {
      const k = String(c.iso3).toUpperCase();
      if (!seen.has(k)) {
        seen.add(k);
        merged.push(c);
      }
    });
    return merged;
  }, [allCountriesWithCoords, labelN]);

  const topBottom3 = useMemo(() => {
    const sorted = [...countries].sort((a, b) => (Number(b.ret) || 0) - (Number(a.ret) || 0));
    const top3 = sorted.slice(0, 3);
    const bottom3 = sorted.slice(Math.max(0, sorted.length - 3));
    return { top3, bottom3 };
  }, [countries]);

  const legendGradient = useMemo(() => {
    const steps = 10;
    const parts = Array.from({ length: steps }, (_, i) => {
      const t = i / (steps - 1);
      const v = retRange.min + (retRange.max - retRange.min) * t;
      const c = colorForValue(v, retRange.min, retRange.max);
      return `${c} ${(t * 100).toFixed(1)}%`;
    });
    return `linear-gradient(90deg, ${parts.join(", ")})`;
  }, [retRange]);

  const Chip = ({ c }) => {
    const { color: fill } = (c.cnfCum3m != null && c.nfpPct != null)
      ? getMarketFlowCategory(c.cnfCum3m, c.nfpPct)
      : { color: marketFlowColors.DEFAULT };
    
    return (
    <div
      key={c.iso3}
      onClick={() => onClick?.(String(c.iso3).toUpperCase())}
      style={{
        cursor: "pointer",
        display: "flex",
        gap: 8,
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid rgba(30,40,34,.10)",
        background: "rgba(255,255,255,.80)",
        fontWeight: 950,
        fontSize: 12,
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 999,
            background: fill,
          border: "1px solid rgba(0,0,0,.15)",
        }}
      />
      <span>{c.code}</span>
      {c.iso3 && <span style={{ color: "var(--muted)", fontSize: 11, fontWeight: 800 }}>{c.iso3}</span>}
      <span style={{ color: "var(--muted)", fontVariantNumeric: "tabular-nums" }}>{fmtPct(c.ret)}</span>
    </div>
  );
  };

  return (
    <div className="td-mapWrap">
      <div className="td-map" style={{ width: "100%" }}>
        <ComposableMap projection="geoMercator" style={{ width: "100%", height: "100%" }}>
          <ZoomableGroup 
            minZoom={1} 
            maxZoom={6} 
            zoom={position.zoom}
            center={position.coordinates}
            onMoveStart={(newPosition) => {
              setPosition(newPosition);
              setZoomLevel(newPosition.zoom || 1);
            }}
            onMoveEnd={(newPosition) => {
              setPosition(newPosition);
              setZoomLevel(newPosition.zoom || 1);
            }}
          >
            <Geographies geography={worldGeo || { type: "FeatureCollection", features: [] }}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const iso3Raw = geo?.id || geo?.properties?.ISO_A3 || geo?.properties?.iso_a3;
                  const iso3 = String(iso3Raw || "").toUpperCase();
                  const country = byIso3.get(iso3);
                  const cnfCum3m = country?.cnfCum3m;
                  const nfpPct = country?.nfpPct;
                  const { color: fill } = (cnfCum3m != null && nfpPct != null) 
                    ? getMarketFlowCategory(cnfCum3m, nfpPct) 
                    : { color: marketFlowColors.DEFAULT };

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onClick={() => (country ? onClick?.(iso3) : undefined)}
                      style={{
                        default: { fill, outline: "none", stroke: "rgba(30,40,34,.10)" },
                        hover: { fill: country == null ? "#ddd9cf" : "#d8d0bf", outline: "none" },
                        pressed: { fill: country == null ? "#ddd9cf" : "#d8d0bf", outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* Afficher tous les pays avec des marqueurs, mais seulement les top/bottom N avec des labels */}
            {allCountriesWithCoords.map((c) => {
              const { color: fill } = (c.cnfCum3m != null && c.nfpPct != null)
                ? getMarketFlowCategory(c.cnfCum3m, c.nfpPct)
                : { color: marketFlowColors.DEFAULT };
              const iso3 = String(c.iso3).toUpperCase();
              const isLabeled = labeledCountries.some(lc => String(lc.iso3).toUpperCase() === iso3);
              
              // Ajuster la taille en fonction du zoom (plus on zoome, plus c'est petit)
              const baseFontSize = 10;
              const baseCircleRadius = 4.5;
              const baseStrokeWidth = 3;
              const fontSize = Math.max(6, baseFontSize / zoomLevel);
              const circleRadius = Math.max(3, baseCircleRadius / zoomLevel);
              const strokeWidth = Math.max(1.5, baseStrokeWidth / zoomLevel);
              
              return (
                <Marker key={iso3} coordinates={[Number(c.lon), Number(c.lat)]}>
                  <g onClick={() => onClick?.(iso3)} style={{ cursor: "pointer" }}>
                    <circle r={circleRadius} fill={fill} stroke="rgba(0,0,0,.25)" strokeWidth={0.8 / zoomLevel} />
                    {isLabeled && (
                      <>
                        <text
                          y={-10 / zoomLevel}
                          textAnchor="middle"
                          style={{
                            fontSize: fontSize,
                            fontWeight: 900,
                            fill: "var(--ink)",
                            paintOrder: "stroke",
                            stroke: "rgba(255,255,255,.86)",
                            strokeWidth: strokeWidth,
                          }}
                        >
                          {c.code || iso3}
                        </text>
                        <text
                          y={2 / zoomLevel}
                          x={8 / zoomLevel}
                          style={{
                            fontSize: fontSize,
                            fontWeight: 900,
                            fill,
                            paintOrder: "stroke",
                            stroke: "rgba(255,255,255,.90)",
                            strokeWidth: strokeWidth,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {fmtPct(c.ret)}
                        </text>
                      </>
                    )}
                  </g>
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>
          </div>

      {/* Legend with colored dots + Top/Bottom 3 (aligned as requested) */}
      <div style={{ marginTop: 14 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          {/* Legend with colored dots - Nouvelle légende Market Flow */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontWeight: 900, fontSize: 13, color: "var(--ink)", marginBottom: 4 }}>{t('Legend')}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                      background: marketFlowColors.ACCUMULATION,
                    border: "1px solid rgba(0,0,0,.15)",
                    boxShadow: "0 2px 4px rgba(16,24,20,.12)",
                  }}
                />
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ink)" }}>{t('ACCUMULATION')}</span>
            </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                      background: marketFlowColors.DISTRIBUTION,
                    border: "1px solid rgba(0,0,0,.15)",
                    boxShadow: "0 2px 4px rgba(16,24,20,.12)",
                  }}
                />
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ink)" }}>{t('DISTRIBUTION')}</span>
              </div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                      background: marketFlowColors.ROTATION_SELL,
                    border: "1px solid rgba(0,0,0,.15)",
                    boxShadow: "0 2px 4px rgba(16,24,20,.12)",
                  }}
                />
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ink)" }}>{t('ROTATION – Sell Pressure')}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: marketFlowColors.ROTATION_BUY,
                      border: "1px solid rgba(0,0,0,.15)",
                      boxShadow: "0 2px 4px rgba(16,24,20,.12)",
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ink)" }}>{t('ROTATION – Buy Support')}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: marketFlowColors.NEUTRAL_LOW,
                      border: "1px solid rgba(0,0,0,.15)",
                      boxShadow: "0 2px 4px rgba(16,24,20,.12)",
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ink)" }}>{t('NEUTRAL – Low Signal')}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: marketFlowColors.NEUTRAL_VOLATILE,
                      border: "1px solid rgba(0,0,0,.15)",
                      boxShadow: "0 2px 4px rgba(16,24,20,.12)",
                    }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 800, color: "var(--ink)" }}>{t('NEUTRAL – Volatile / Test')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Label selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: "var(--muted)", marginTop: -20 }}>Labels:</div>
            <select
              className="td-select"
              value={String(labelN)}
              onChange={(e) => {
                const val = e.target.value;
                setLabelN(val === "all" ? "all" : Number(val));
              }}
              aria-label="Label count"
            >
              {[3, 5, 6, 8, 10, 15, 20, 25].map((n) => (
                <option key={n} value={String(n)}>
                  Top/Bottom {n}
                </option>
              ))}
              <option value="all">Tous les pays</option>
            </select>
            </div>
          </div>

        {/* Top 3 on one line, Bottom 3 on the next line */}
        <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 950, color: "var(--muted)", width: 80 }}>Top 3</div>
            {topBottom3.top3.map((c) => (
              <Chip key={c.iso3 + "-top"} c={c} />
            ))}
      </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ fontSize: 12, fontWeight: 950, color: "var(--muted)", width: 80 }}>Bottom 3</div>
            {topBottom3.bottom3.map((c) => (
              <Chip key={c.iso3 + "-bottom"} c={c} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Table View ---------------- */
function ColoredValue({ value, kind, cnfCum3m, nfpPct }) {
  const v = Number(value) || 0;
  const text = fmtPct(v); // Toujours en pourcentages pour Market Flow
  
  // Si cnfCum3m et nfpPct sont fournis, utiliser la logique Market Flow
  let textColor = "var(--ink)"; // Noir par défaut
  if (cnfCum3m != null && nfpPct != null) {
    const { color } = getMarketFlowCategory(cnfCum3m, nfpPct);
    textColor = color;
  }
  
  return <span style={{ color: textColor, fontWeight: 950, fontVariantNumeric: "tabular-nums" }}>{text}</span>;
}

function TableCard({ title, rows, onRowClick }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(1);
  const [sortDir, setSortDir] = useState("asc");
  const isSectors = title === t('sectors') || title === 'Secteurs' || title === 'Sectors';

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) => {
      const a = String(r.raw?.a || "");
      return a.toLowerCase().includes(query);
    });
  }, [rows, q]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((x, y) => {
      if (sortKey === 1) {
        const a = String(x.raw?.a || "");
        const b = String(y.raw?.a || "");
        return dir * a.localeCompare(b);
      }
      if (sortKey === 2) return dir * ((x.raw?.b || 0) - (y.raw?.b || 0));
      return dir * ((x.raw?.c || 0) - (y.raw?.c || 0));
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === 1 ? "asc" : "desc");
    }
  };

  return (
    <div className={`td-tableCard ${isSectors ? 'td-tableCard-noScroll' : ''}`}>
      <div className="td-tableHead">
        <div className="td-tableTitle">{title}</div>
        <input className="td-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} />
      </div>

      <div className={`td-tableWrap ${isSectors ? 'td-tableWrap-noScroll' : ''}`}>
        <table className="td-table">
          <thead>
            <tr>
              <th onClick={() => toggleSort(1)} style={{ cursor: "pointer" }}>
                {t('item')} {sortKey === 1 ? (sortDir === "asc" ? "▲" : "▼") : ""}
              </th>
              <th onClick={() => toggleSort(2)} style={{ cursor: "pointer" }}>
                {t('cnfLabel')} {sortKey === 2 ? (sortDir === "asc" ? "▲" : "▼") : ""}
              </th>
              <th onClick={() => toggleSort(3)} style={{ cursor: "pointer" }}>
                {t('nfpLabel')} {sortKey === 3 ? (sortDir === "asc" ? "▲" : "▼") : ""}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, idx) => {
              // Appliquer un fond coloré si bgColor est disponible (pour les régions)
              const bgColor = r.raw?.bgColor;
              const rowStyle = bgColor 
                ? { 
                    cursor: onRowClick ? 'pointer' : 'default',
                    background: `linear-gradient(90deg, ${bgColor}15, transparent 65%)`,
                  }
                : { cursor: onRowClick ? 'pointer' : 'default' };
              
              return (
              <tr 
                key={idx}
                onClick={() => onRowClick && onRowClick(r.raw?.a)}
                  style={rowStyle}
                title={onRowClick ? drillRowTitle(r.raw?.investorComment) : undefined}
              >
                <td>{r.a}</td>
                <td>{r.b}</td>
                <td>{r.c}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TableView({ data, onDrill }) {
  const { t, translateData, language } = useTranslation();
  
  const regionRows = useMemo(() => {
    const regions = data.regions || [];
    return regions.map((r) => {
      const regionName = translateData(r.region || r.key || 'N/A');
      const value = r.value || r.ret || 0; // CNF_cum_3M
      const delta = r.changePct || r.delta || 0; // NFP_pct déjà en %
      const { color: bgColor } = getMarketFlowCategory(value, delta);
      return {
        a: <span style={{ fontWeight: 950 }}>{regionName}</span>,
        b: <ColoredValue value={value} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        c: <ColoredValue value={delta} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        raw: { a: r.region || r.key || regionName, b: value, c: delta, bgColor, investorComment: r.investorComment || null },
      };
    });
  }, [data.regions, translateData, language]);

  const countryRows = useMemo(() => {
    // Utiliser countriesRaw si disponible (données brutes), sinon countries (transformées pour la carte)
    const countriesData = data.countriesRaw || data.countries || [];
    return countriesData.map((c) => {
      // Prioriser country (ISO2) pour le drill-down, puis iso3, puis code
      const countryCode = c.country || c.iso3 || c.code || 'N/A';
      const countryName = c.iso3 || c.country || c.code || 'N/A';
      const value = c.value || c.ret || 0; // CNF_cum_3M
      const delta = c.changePct || 0; // NFP_pct déjà en %
      return {
        a: <span style={{ fontWeight: 950 }}>{countryName}</span>,
        b: <ColoredValue value={value} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        c: <ColoredValue value={delta} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        raw: { a: countryCode, b: value, c: delta, investorComment: c.investorComment || null },
      };
    });
  }, [data.countriesRaw, data.countries, language]);

  const sectorRows = useMemo(() => {
    return (data.sectorRotation || []).map((s) => {
      const sectorName = translateData(s.name || s.sector || 'N/A');
      const sectorKey = s.name || s.sector || sectorName; // Garder la valeur originale pour le drill-down
      const value = s.ret || s.value || 0; // CNF_cum_3M
      const delta = s.deltaBp || s.changePct || 0; // NFP_pct
      return {
        a: <span style={{ fontWeight: 950 }}>{sectorName}</span>,
        b: <ColoredValue value={value} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        c: <ColoredValue value={delta} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        raw: { a: sectorKey, b: value, c: delta, investorComment: s.investorComment || null },
      };
    });
  }, [data.sectorRotation, translateData, language]);

  const marketCapRows = useMemo(() => {
    return data.marketCaps.map((k) => {
      const title = translateData(k.title || 'N/A');
      const tag = translateData(k.tag || '');
      const marketCapKey = k.title || title; // Garder la valeur originale pour le drill-down
      const value = k.val || 0; // CNF_cum_3M
      const delta = k.delta || 0; // NFP_pct
      return {
        a: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 950 }}>{title}</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 900,
                color: "var(--muted)",
                border: "1px solid rgba(30,40,34,.10)",
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(181,154,91,.08)",
              }}
            >
              {tag}
            </span>
          </div>
        ),
        b: <ColoredValue value={value} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        c: <ColoredValue value={delta} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        raw: { a: marketCapKey, b: value, c: delta, investorComment: k.investorComment || null },
      };
    });
  }, [data.marketCaps, translateData, language]);

  const fundamentalRows = useMemo(() => {
    return data.fundamentals.map((k) => {
      const title = translateData(k.title || 'N/A');
      const tag = translateData(k.tag || '');
      const fundamentalKey = k.title || title; // Garder la valeur originale pour le drill-down
      const value = k.val || 0; // CNF_cum_3M
      const delta = k.delta || 0; // NFP_pct
      return {
        a: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 950 }}>{title}</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 900,
                color: "var(--muted)",
                border: "1px solid rgba(30,40,34,.10)",
                padding: "2px 8px",
                borderRadius: 999,
                background: "rgba(181,154,91,.08)",
              }}
            >
              {tag}
            </span>
          </div>
        ),
        b: <ColoredValue value={value} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        c: <ColoredValue value={delta} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        raw: { a: fundamentalKey, b: value, c: delta, investorComment: k.investorComment || null },
      };
    });
  }, [data.fundamentals, translateData, language]);

  const styleRows = useMemo(() => {
    return data.styles.map((s) => {
      const styleName = translateData(s.name || 'N/A');
      const styleKey = s.name || styleName; // Garder la valeur originale pour le drill-down
      // Pour les styles, share est le CNF et deltaBp est le NFP
      const value = s.share || 0; // CNF_cum_3M (approximation avec share)
      const delta = s.deltaBp || 0; // NFP_pct
      return {
        a: <span style={{ fontWeight: 950 }}>{styleName}</span>,
        b: <ColoredValue value={value} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        c: <ColoredValue value={delta} kind="pct" cnfCum3m={value} nfpPct={delta} />,
        raw: { a: styleKey, b: value, c: delta, investorComment: s.investorComment || null },
      };
    });
  }, [data.styles, translateData, language]);

  // Fonction pour gérer le clic sur une ligne selon le type de table
  const handleRowClick = (tableTitle, value) => {
    if (!onDrill || !value) return;
    
    const titleLower = tableTitle.toLowerCase();
    if (titleLower.includes('region') || titleLower.includes('région')) {
      onDrill('Region', value);
    } else if (titleLower.includes('country') || titleLower.includes('pays')) {
      onDrill('Country', value);
    } else if (titleLower.includes('sector') || titleLower.includes('secteur')) {
      onDrill('Sector', value);
    } else if (titleLower.includes('marketcap') || titleLower.includes('capitalisation')) {
      onDrill('MarketCap', value);
    } else if (titleLower.includes('fundamental') || titleLower.includes('fondamental')) {
      onDrill('Fundamental', value);
    } else if (titleLower.includes('style')) {
      onDrill('InvestingStyle', value);
    }
  };

  return (
    <div className="td-tableGrid">
      <TableCard title={t('regions')} rows={regionRows} onRowClick={(value) => handleRowClick(t('regions'), value)} />
      <TableCard title={t('countries')} rows={countryRows} onRowClick={(value) => handleRowClick(t('countries'), value)} />
      <TableCard title={t('sectors')} rows={sectorRows} onRowClick={(value) => handleRowClick(t('sectors'), value)} />
      <TableCard title={t('marketCap')} rows={marketCapRows} onRowClick={(value) => handleRowClick(t('marketCap'), value)} />
      <TableCard title={t('fundamentalScore')} rows={fundamentalRows} onRowClick={(value) => handleRowClick(t('fundamentalScore'), value)} />
      <TableCard title={t('styles')} rows={styleRows} onRowClick={(value) => handleRowClick(t('styles'), value)} />
    </div>
  );
}

/* ---------------- Drill Modal ---------------- */
function DrillDownModal({ open, ctx, data, loading, onClose, onSelectAsset }) {
  const { t, translateData } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  useDrillFilterStyles();

  const scopeKey = ctx
    ? `${ctx.scopeType}|${ctx.scopeValue}|${ctx.asOfLabel || ""}`
    : "";
  const assetFilter = useDrillAssetFilters(data?.topAssets, scopeKey);
  const topAssets = assetFilter.filtered;

  useEffect(() => {
    const onR = () => setIsMobile(typeof window !== "undefined" ? window.innerWidth <= 720 : false);
    onR();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !ctx) return null;

  const fundBg = {
    // Legacy English labels
    Excellent: tone.green,
    Good: tone.green2,
    Average: tone.amber,
    Bad: tone.orange,
    Worst: tone.red,
    // New French labels
    Solide: tone.green2, // Bon
    Satisfaisant: tone.amber, // Moyen
    Fragile: tone.orange, // Mauvais
    Insuffisant: tone.red, // Pire
    // Keep explicit French source labels too
    Bon: tone.green2,
    Moyen: tone.amber,
    Mauvais: tone.orange,
    Pire: tone.red,
  };

  const BadgeCap = ({ label }) => {
    if (!label || label.trim() === '') return null;
    return <span className="td-badge td-badgeCap">{translateData(label)}</span>;
  };

  const BadgeFund = ({ label }) => {
    if (!label || label.trim() === '') return null;
    return (
      <span
        className="td-badge td-badgeFund"
        style={{
          background: fundBg[label] || "rgba(31,42,36,.20)",
          color: '#fff',
        }}
      >
        {translateData(label)}
      </span>
    );
  };

  const valueColor = (v) => {
    if (!Number.isFinite(v) || v === 0) return "var(--muted)";
    return v > 0 ? tone.green : tone.red;
  };

  return (
    <div className="td-modalOverlay" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="td-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="td-modalHeader">
          <div className="td-modalTitle">
            <div className="td-modalTopRow">
              <button className="td-backMini" onClick={onClose} title={t('backToTopDown')}>
                ← {t('backToTopDown')}
              </button>
            </div>

            <h3>{translateData(ctx.scopeValue)}</h3>
            <div className="td-modalMeta">
              {t('scope')}: {ctx.scopeType} · {ctx.asOfLabel}
            </div>
          </div>

          <button className="td-modalClose" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="td-modalBody">
          {loading ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--muted)" }}>
              {t('loadingMarketData')}
            </div>
          ) : data ? (
            <>
              {/* Scope KPIs - Utiliser les valeurs du contexte (de la page principale) */}
              <div className="td-scopeBar">
                <div className="td-scopeKpi">
                  <span>{t('cnfLabel')}</span>
                  <b style={{ color: "var(--ink)" }}>{fmtPct(ctx.scopeCnf || 0)}</b>
                </div>
                <div className="td-scopeKpi">
                  <span>{t('nfpLabel')}</span>
                  {ctx.scopeNfp !== null && ctx.scopeNfp !== undefined ? (
                    <b style={{ color: "var(--ink)" }}>{fmtPct(ctx.scopeNfp)}</b>
                  ) : (
                    <b style={{ color: "var(--muted)" }}>N/A</b>
                  )}
                </div>
              </div>

              <div style={{ fontWeight: 950, fontSize: 16, margin: "10px 2px 10px" }}>
                Top {translateData(ctx.scopeValue)} {t('assets')}
              </div>

              <DrillDownFilterBar t={t} translateData={translateData} filter={assetFilter} />

              {topAssets.length === 0 ? (
                <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--muted)", fontWeight: 800 }}>
                  {t('noMatchingAssets')}
                </div>
              ) : isMobile ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {topAssets.map((r, idx) => (
                    <div
                      key={idx}
                      role={onSelectAsset ? "button" : undefined}
                      tabIndex={onSelectAsset ? 0 : undefined}
                      onClick={onSelectAsset ? () => onSelectAsset(r.asset) : undefined}
                      onKeyDown={onSelectAsset ? (e) => { if (e.key === "Enter" || e.key === " ") onSelectAsset(r.asset); } : undefined}
                      style={{
                        borderRadius: 16,
                        border: "1px solid rgba(30,40,34,.10)",
                        background: "rgba(255,255,255,.82)",
                        boxShadow: "0 10px 22px rgba(16,24,20,.08)",
                        padding: 12,
                        cursor: onSelectAsset ? "pointer" : "default",
                      }}
                    >
                      {/* Line 1: Asset | Region | Country   Market Cap right */}
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 950 }}>{r.asset}</div>
                          <div style={{ fontSize: 12, color: "var(--muted)", fontWeight: 900 }}>
                            {translateData(r.region)} | {translateData(r.country)}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          {r.marketCap ? <BadgeCap label={r.marketCap} /> : <span style={{ color: "var(--muted)", fontSize: 12 }}>N/A</span>}
                        </div>
                      </div>

                      {/* Line 2: Sector | Style   Fundamental right */}
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 900, color: "var(--ink)", minWidth: 0 }}>
                          {translateData(r.sector || 'N/A')} | {translateData(r.style || 'N/A')}
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          {r.fundamental ? <BadgeFund label={r.fundamental} /> : <span style={{ color: "var(--muted)", fontSize: 12 }}>N/A</span>}
                        </div>
                      </div>

                      {/* Line 3: CNF / NFP with color coding on numbers */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          marginTop: 12,
                          paddingTop: 10,
                          borderTop: "1px solid rgba(30,40,34,.08)",
                          fontSize: 13,
                          fontWeight: 900,
                        }}
                      >
                        <div>
                          <span style={{ color: "var(--muted)" }}>{t('cnfLabel')}:</span>{" "}
                          <span className="td-num" style={{ color: "var(--ink)" }}>
                            {fmtPct(r.cnfPct || 0)}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: "var(--muted)" }}>{t('nfpLabel')}:</span>{" "}
                          {r.nfpPct !== null && r.nfpPct !== undefined ? (
                            <span className="td-num" style={{ color: "var(--ink)" }}>
                              {fmtPct(r.nfpPct)}
                            </span>
                          ) : (
                            <span style={{ color: "var(--muted)" }}>N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <table className="td-drillTable">
                  <thead>
                    <tr>
                      <th>{t('asset')}</th>
                      <th>{t('regions')}</th>
                      <th>{t('countries')}</th>
                      <th>{t('sectors')}</th>
                      <th>{t('styles')}</th>
                      <th>{t('marketCap')}</th>
                      <th>{t('fundamentalScore')}</th>
                      <th>{t('cnfLabel')}</th>
                      <th>{t('nfpLabel')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topAssets.map((r, idx) => (
                      <tr
                        key={idx}
                        role={onSelectAsset ? "button" : undefined}
                        tabIndex={onSelectAsset ? 0 : undefined}
                        onClick={onSelectAsset ? () => onSelectAsset(r.asset) : undefined}
                        onKeyDown={onSelectAsset ? (e) => { if (e.key === "Enter" || e.key === " ") onSelectAsset(r.asset); } : undefined}
                        style={{ cursor: onSelectAsset ? "pointer" : "default" }}
                      >
                        <td style={{ fontWeight: 950 }}>{r.asset}</td>
                        <td>{translateData(r.region || 'N/A')}</td>
                        <td>{translateData(r.country || 'N/A')}</td>
                        <td>{translateData(r.sector || 'N/A')}</td>
                        <td>{translateData(r.style || 'N/A')}</td>
                        <td>
                          {r.marketCap ? <BadgeCap label={r.marketCap} /> : <span style={{ color: "var(--muted)" }}>N/A</span>}
                        </td>
                        <td>
                          {r.fundamental ? <BadgeFund label={r.fundamental} /> : <span style={{ color: "var(--muted)" }}>N/A</span>}
                        </td>
                        <td className="td-num" style={{ color: "var(--ink)" }}>
                          {fmtPct(r.cnfPct || 0)}
                        </td>
                        <td className="td-num" style={{ color: r.nfpPct !== null && r.nfpPct !== undefined ? "var(--ink)" : "var(--muted)" }}>
                          {r.nfpPct !== null && r.nfpPct !== undefined ? fmtPct(r.nfpPct) : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)", fontWeight: 800 }}>
                {t('tip')}: {t('pressEscToClose')}
              </div>
            </>
          ) : (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--muted)" }}>
              {t('noDataAvailable')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Landing;
